/* Wix redirect builder. Turns old and new page addresses into the CSV file
   that Wix's URL Redirect Manager imports, after catching what makes
   redirects fail or hurt: chains, loops, duplicates, conflicting targets,
   pages sent to the home page and files over Wix's limits. It can also build
   the list for a store moving to Wix: match two lists of addresses by name,
   or swap the old platform's folders for Wix's and keep each name.

   The rules are plain functions, so the tests and the server-rendered first
   paint (wix-redirect-builder.cjs) run the same code as the browser. Nothing
   here fetches, opens or stores an address. Wix and Google facts come from
   their own pages (listed in the page's Sources); where Wix doesn't document
   something (capitals, ?query parts, a / at the end), the tool says "test
   one" instead of guessing. */
(function (root, factory) {
  'use strict';
  const shared = typeof require === 'function' && typeof module === 'object' ? require('./wix-shared.js') : root.WixShared;
  const api = factory(shared);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.WixRedirects = api;
  if (typeof document !== 'undefined' && typeof document.querySelector === 'function') {
    const start = () => api.init(document.querySelector('[data-wrb-lab]'));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (shared) {
  'use strict';

  const { esc, tidyText, parseDelimited, detectDelimiter, toCsv } = shared;

  const META = Object.freeze({
    path: '/tools/wix-redirect-builder',
    name: 'Wix redirect builder',
    summary: 'Turn old and new addresses into a file for Wix’s URL Redirect Manager, and catch chains, loops and duplicates first.'
  });

  // Wix Help Center: "each file can include a maximum of 500 redirects; any
  // redirects listed beyond this limit will not be imported" and "You can
  // create a maximum of 5,000 redirects per Wix site."
  const PER_FILE = 500;
  const PER_SITE = 5000;
  // Wix's developer docs: a redirect's `from` is at most 950 characters and
  // its `to` at most 4,096.
  const OLD_MAX = 950;
  const NEW_MAX = 4096;
  // How much of a pasted list is read. Far past Wix's 5,000-per-site cap,
  // and still quick to check in a browser.
  const MAX_LINES = 20000;
  // Wix publishes no text of its template's header row, so the file uses the
  // labels of Wix's own redirect dialog ("Old URL", "New URL").
  const CSV_HEADER = Object.freeze(['Old URL', 'New URL']);
  // Wix's default addresses: /product-page/{slug}, /category/{slug},
  // /post/{slug}, and /{slug} for other pages.
  const DEFAULT_PREFIXES = Object.freeze({ product: 'product-page', category: 'category', post: 'post' });
  // A group redirect is suggested when at least this many rows make the same
  // folder change with the rest of the address unchanged, and they are at
  // least this share of the rows in that old folder.
  const GROUP_MIN = 5;
  const GROUP_SHARE = 0.9;
  const PAGE_SIZE = 100;

  // ---- Samples (made-up stores) ---------------------------------------------

  // Juniper & Oak Candle Co. moving from Shopify. Lines 7 and 8 make a chain,
  // lines 4 and 9 are the same redirect, 10 and 11 send one old address to two
  // pages, and 12 sends a retired product to the home page.
  const SAMPLE = [
    'Old URL,New URL',
    '/products/beeswax-pillar-candle,/product-page/beeswax-pillar-candle',
    '/collections/candles/products/beeswax-pillar-candle,/product-page/beeswax-pillar-candle',
    '/products/cedar-and-sage-candle,/product-page/cedar-sage-candle',
    '/collections/candles,/category/candles',
    '/pages/our-story,/about',
    '/products/lavender-tin,/product-page/lavender-travel-tin',
    '/product-page/lavender-travel-tin,/product-page/lavender-tin-candle',
    '/products/cedar-and-sage-candle,/product-page/cedar-sage-candle',
    '/products/gift-card,/product-page/e-gift-card',
    '/products/gift-card,/product-page/gift-card',
    '/products/holiday-sampler-2025,/'
  ].join('\n');

  // The same store's old Shopify sitemap and its new Wix sitemap, still on
  // the free Wix address before the domain moves.
  const SAMPLE_OLD = [
    'https://juniperandoak.example/products/beeswax-pillar-candle',
    'https://juniperandoak.example/products/cedar-and-sage-candle',
    'https://juniperandoak.example/products/lavender-tin',
    'https://juniperandoak.example/products/linen-wick-trimmer',
    'https://juniperandoak.example/products/holiday-sampler-2025',
    'https://juniperandoak.example/collections/candles',
    'https://juniperandoak.example/collections/gifts',
    'https://juniperandoak.example/pages/our-story',
    'https://juniperandoak.example/blogs/journal/how-to-trim-a-wick'
  ].join('\n');
  const SAMPLE_NEW = [
    'https://juniperandoak.wixsite.com/shop/product-page/beeswax-pillar-candle',
    'https://juniperandoak.wixsite.com/shop/product-page/cedar-sage-candle',
    'https://juniperandoak.wixsite.com/shop/product-page/lavender-travel-tin',
    'https://juniperandoak.wixsite.com/shop/product-page/wick-trimmer',
    'https://juniperandoak.wixsite.com/shop/category/candles',
    'https://juniperandoak.wixsite.com/shop/category/gift-sets',
    'https://juniperandoak.wixsite.com/shop/about',
    'https://juniperandoak.wixsite.com/shop/post/how-to-trim-a-wick'
  ].join('\n');
  // Northfield Tea Supply, moving from Shopify with the same product names.
  const SAMPLE_PATTERN = [
    'https://northfieldtea.example/products/assam-breakfast',
    'https://northfieldtea.example/products/darjeeling-first-flush',
    'https://northfieldtea.example/products/genmaicha',
    'https://northfieldtea.example/products/hojicha-roast',
    'https://northfieldtea.example/products/lapsang-souchong',
    'https://northfieldtea.example/products/cast-iron-teapot',
    'https://northfieldtea.example/products/genmaicha?variant=4211',
    'https://northfieldtea.example/collections/green-tea',
    'https://northfieldtea.example/collections/green-tea/products/genmaicha',
    'https://northfieldtea.example/pages/brewing-guide',
    'https://northfieldtea.example/blogs/journal/how-long-to-steep-green-tea'
  ].join('\n');
  const SAMPLES = Object.freeze({ pairs: SAMPLE, old: SAMPLE_OLD, new: SAMPLE_NEW, pattern: SAMPLE_PATTERN });

  // ---- Small text helpers ----------------------------------------------------

  const text = value => (value == null ? '' : String(value));
  const number = count => Number(count).toLocaleString('en-US');
  const plural = (count, one, many = `${one}s`) => `${number(count)} ${count === 1 ? one : many}`;
  const joinWords = words => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);
  // An address may break after / ? & = _ . or -, so a long one wraps at a
  // folder instead of mid-word. Each piece is escaped on its own.
  function breakable(value) {
    const chars = Array.from(text(value));
    const parts = [];
    let current = '';
    chars.forEach((char, index) => {
      current += char;
      // Not after a leading "/" or the first of "//": a lone slash at the
      // end of a line reads as a stray mark.
      if ('/?&=_.-'.includes(char) && index > 0 && chars[index + 1] !== '/' && current.replace(/\//g, '')) {
        parts.push(current);
        current = '';
      }
    });
    if (current) parts.push(current);
    return parts.map(esc).join('<wbr>');
  }
  const code = value => `<code>${breakable(value)}</code>`;
  // Short paths in a sentence stay on one line ("/products/gift-card" broke
  // at its hyphen on phones).
  const keepPaths = html => html.replace(/\/[A-Za-z0-9\-_~%/]*[A-Za-z0-9\-_~%/]/g, match => (match.length <= 32 ? `<span class="wrb-nowrap">${match}</span>` : match));
  const unicodePattern = (source, flags, fallback) => {
    try { return new RegExp(source, flags); } catch (_) { return fallback; }
  };
  const UPPER = unicodePattern('\\p{Lu}', 'u', /[A-ZÀ-Þ]/);
  const WORD_SPLIT = unicodePattern('[^\\p{L}\\p{N}]+', 'u', /[^A-Za-z0-9À-ɏ]+/);
  const NOT_WORD = unicodePattern('[^\\p{L}\\p{N}]+', 'gu', /[^A-Za-z0-9À-ɏ]+/g);
  // Slashes off both ends, by index: /\/+$/ backtracks on long runs.
  function trimSlashes(value) {
    const typed = text(value).trim();
    let start = 0;
    let end = typed.length;
    while (start < end && typed[start] === '/') start += 1;
    while (end > start && typed[end - 1] === '/') end -= 1;
    return typed.slice(start, end);
  }
  const safeDecode = value => {
    try { return decodeURIComponent(value); } catch (_) { return value; }
  };

  // A long address in a button label keeps its start and its end.
  function shorten(value, max = 52) {
    const chars = Array.from(text(value));
    if (chars.length <= max) return chars.join('');
    const head = Math.ceil((max - 1) * 0.55);
    return `${chars.slice(0, head).join('')}…${chars.slice(chars.length - (max - 1 - head)).join('')}`;
  }

  // Cells arrive from spreadsheets, Word and web pages: tidyText turns
  // no-break spaces into spaces and drops zero-width marks; quotes or angle
  // brackets around a whole address go too.
  const WRAPS = [['"', '"'], ["'", "'"], ['“', '”'], ['‘', '’'], ['<', '>'], ['«', '»']];
  function cleanCell(value) {
    let out = tidyText(value);
    for (let round = 0; round < 3; round += 1) {
      const pair = WRAPS.find(([open, close]) => out.length > 1 && out.startsWith(open) && out.endsWith(close));
      if (!pair) break;
      out = out.slice(pair[0].length, out.length - pair[1].length).trim();
    }
    return out;
  }

  // ---- Reading a pasted list of pairs -----------------------------------------

  // No \s* around the arrow: on a long run of spaces that backtracked for
  // seconds. The cells are trimmed afterwards (cleanCell).
  const ARROW = /-{1,2}>|={1,2}>|[→⇒⟶⟹➔➜➝⇨]|\s>\s/;
  const HEADER_WORDS = new Set(['old', 'new', 'url', 'urls', 'uri', 'address', 'addresses', 'link', 'links', 'from', 'to', 'source', 'target', 'destination', 'redirect', 'redirects', 'redirected', 'path', 'paths', 'page', 'pages', 'original', 'current', 'final', 'previous', 'location', 'web', 'site', 'the', 'a', 'of']);
  const FILE_ENDINGS = /^(?:s?html?|php\d?|aspx?|jsp|cfm|cgi|pl|xml|txt|json|pdf|jpe?g|png|gif|webp|avif|svg|css|js|mp[34]|mov|webm|zip|csv|ico|rss|atom)$/i;

  // "shop.example" or "www.shop.example:8080", but not "about.html".
  function looksLikeHost(segment) {
    const value = text(segment);
    if (!/^[a-z0-9-]+(?:\.[a-z0-9-]+)+\.?(?::\d+)?$/i.test(value)) return false;
    const ending = value.replace(/:\d+$/, '').replace(/\.$/, '').split('.').pop();
    return /^[a-z]{2,24}$/i.test(ending) && !FILE_ENDINGS.test(ending);
  }
  const looksLikeAddress = token => /^(?:https?:\/\/|\/)/i.test(token) || looksLikeHost(text(token).split(/[/?#]/)[0]);

  function isHeaderCell(cell) {
    if (!cell || /[/.]/.test(cell) || !/[a-z]/i.test(cell)) return false;
    return cell.toLowerCase().split(/[^a-z]+/).filter(Boolean).every(word => HEADER_WORDS.has(word));
  }
  // A heading is judged on its first two cells, so a third column ("Old
  // URL,New URL,Notes", "Redirect from,Redirect to,Type") doesn't hide it.
  // Two cells of words with spaces and no / or . are a heading in any
  // language ("Ancienne URL;Nouvelle URL", "URL antigua,URL nueva").
  function isHeader(cells) {
    const firstTwo = cells.slice(0, 2).filter(Boolean);
    if (!firstTwo.length) return false;
    if (firstTwo.every(isHeaderCell)) return true;
    return firstTwo.length === 2 && firstTwo.every(cell => /\s/.test(cell) && !/[/.]/.test(cell));
  }
  // "# old products" or "// moved in May" is a note, not a redirect. A line
  // that starts "//shop.example/…" is an address without https:.
  function isComment(line) {
    const typed = line.trim();
    if (typed.startsWith('#')) return true;
    if (!typed.startsWith('//')) return false;
    return !looksLikeHost(typed.slice(2).split(/[/?#\s,;]/)[0]);
  }
  const SITEMAP = /<urlset|<sitemapindex/i;

  // Spreadsheets paste as tabs; otherwise commas or semicolons (many
  // countries' Excel saves semicolons), as the shared helper decides.
  function pickDelimiter(lines) {
    if (lines.some(line => line.includes('\t'))) return '\t';
    const found = detectDelimiter(lines.slice(0, 40).join('\n'));
    return found === '\t' ? ',' : found;
  }

  // One line into cells. A tab wins, then an arrow ("->", "=>", "→"), then
  // the list's comma or semicolon (quoted CSV fields included), then spaces.
  function splitRecord(line, delimiter) {
    if (line.includes('\t')) return { cells: parseDelimited(line, '\t')[0] || [] };
    const arrow = ARROW.exec(line);
    if (arrow) return { cells: [line.slice(0, arrow.index), line.slice(arrow.index + arrow[0].length)] };
    const separator = line.includes(delimiter) ? delimiter : [',', ';'].find(sep => line.includes(sep));
    if (separator) return { cells: parseDelimited(line, separator)[0] || [] };
    const tokens = line.trim().split(/\s+/);
    if (tokens.length <= 2) return { cells: tokens };
    const addresses = tokens.filter(looksLikeAddress);
    if (addresses.length === 2) return { cells: addresses };
    return { cells: tokens, unclear: true };
  }

  // Pasted pairs into rows: { line, from, to }. The first line is skipped
  // when it reads like a heading ("Old URL,New URL", "Redirect from,Redirect
  // to"); options.header === false keeps it. A byte-order mark, CRLF and
  // blank lines are fine. Lines past MAX_LINES are not read.
  function parseInput(input, options = {}) {
    const source = text(input).replace(/^﻿/, '');
    let lines = source.split(/\r\n|\n|\r/);
    const truncated = lines.length > MAX_LINES;
    if (truncated) lines = lines.slice(0, MAX_LINES);
    // A sitemap's text is a list of single addresses, not pairs.
    if (SITEMAP.test(source)) return { rows: [], header: null, delimiter: ',', truncated: false, lineCount: lines.length, comments: 0, sitemap: true };
    const delimiter = pickDelimiter(lines);
    const rows = [];
    let header = null;
    let comments = 0;
    lines.forEach((raw, index) => {
      if (!raw.trim()) return;
      if (isComment(raw)) {
        comments += 1;
        return;
      }
      const split = splitRecord(raw, delimiter);
      const cells = split.cells.map(cleanCell);
      while (cells.length && !cells[cells.length - 1]) cells.pop();
      if (!cells.length) return;
      if (!rows.length && !header && options.header !== false && isHeader(cells)) {
        header = { line: index + 1, cells };
        return;
      }
      const row = { line: index + 1, from: cells[0] || '', to: cells[1] || '' };
      const extra = cells.slice(2).filter(Boolean).length;
      if (extra) row.extra = extra;
      if (split.unclear) row.unclear = true;
      rows.push(row);
    });
    return { rows, header, delimiter, truncated, lineCount: lines.length, comments, sitemap: false };
  }

  // ---- Reading one address -----------------------------------------------------

  const FREE_WIX_HOST = /(?:^|\.)(?:wixsite\.com|wixstudio\.com|wixstudio\.io)$/i;
  const bareHost = host => text(host).toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '').replace(/^www\./, '');
  // Wix's developer docs count a redirect's addresses URL-encoded (é is
  // %C3%A9, six characters). One already encoded is not counted twice.
  function encodedLength(value) {
    try { return encodeURI(decodeURI(value)).length; } catch (_) { /* a stray % */ }
    try { return encodeURI(value).length; } catch (_) { return text(value).length; }
  }

  // "https://www.shop.example/" or "shop.example" to "shop.example".
  function siteHost(value) {
    const typed = cleanCell(value);
    if (!typed) return '';
    const host = typed.replace(/^[a-z][a-z0-9+.-]*:\/*/i, '').replace(/^\/\//, '').split(/[/?#\s]/)[0].replace(/^[^@]*@/, '');
    return looksLikeHost(host) ? bareHost(host) : '';
  }

  function hostOf(value) {
    const typed = cleanCell(value);
    const full = /^(?:https?:)?\/\/([^/?#]+)/i.exec(typed);
    if (full) return bareHost(full[1].replace(/^[^@]*@/, ''));
    const first = typed.split(/[/?#]/)[0];
    return !typed.startsWith('/') && looksLikeHost(first) ? bareHost(first) : '';
  }

  // One address as the Redirect Manager needs it. Wix: "Only the path is
  // needed" for the old address; the new one is "a path on the site" or "the
  // entire URL" of an outside page. The query and #fragment keep their
  // spelling; only the path is tidied (doubled slashes, a / at the end).
  // Returns { typed, kind: path | external | empty | invalid, value, host,
  // path, query, fragment, tidy: [...], flags: {...} }.
  function readAddress(raw, role, context = {}) {
    const typed = cleanCell(raw);
    const address = { typed, role, kind: 'path', value: '', host: '', path: '', query: '', fragment: null, tidy: [], flags: {} };
    if (!typed) {
      address.kind = 'empty';
      return address;
    }
    let rest = typed;
    let host = '';
    let scheme = '';
    // "shop.example:8080/page" has a port, not a scheme.
    const schemeMatch = /^([a-z][a-z0-9+.-]*):(?!\d)/i.exec(typed);
    if (schemeMatch) {
      scheme = schemeMatch[1].toLowerCase();
      const parts = /^[a-z][a-z0-9+.-]*:\/*([^/?#]*)([\s\S]*)$/i.exec(typed);
      if ((scheme !== 'http' && scheme !== 'https') || !parts || !parts[1]) {
        address.kind = 'invalid';
        address.scheme = scheme;
        return address;
      }
      host = parts[1];
      rest = parts[2];
    } else if (/^\/\/[^/]/.test(typed)) {
      const parts = /^\/\/([^/?#]*)([\s\S]*)$/.exec(typed);
      if (looksLikeHost(parts[1])) {
        host = parts[1];
        rest = parts[2];
        scheme = 'https';
      }
    } else if (!typed.startsWith('/')) {
      const first = typed.split(/[/?#]/)[0];
      if (looksLikeHost(first)) {
        host = first;
        rest = typed.slice(first.length);
        scheme = 'https';
      }
    }
    if (host) {
      host = host.replace(/^[^@]*@/, '').toLowerCase();
      address.host = host;
      address.scheme = scheme;
    }
    let path = rest;
    let query = '';
    let fragment = null;
    const hashAt = path.indexOf('#');
    if (hashAt !== -1) {
      fragment = path.slice(hashAt + 1);
      path = path.slice(0, hashAt);
    }
    const queryAt = path.indexOf('?');
    if (queryAt !== -1) {
      query = path.slice(queryAt + 1);
      path = path.slice(0, queryAt);
      if (!query) address.tidy.push('empty-query');
    }
    const siteHosts = context.siteHosts || new Set();
    const freeWix = Boolean(host) && FREE_WIX_HOST.test(host);
    const own = Boolean(host) && ((context.ownAllNew && role === 'new') || siteHosts.has(bareHost(host)));
    if (host && role === 'new' && !own && !freeWix) {
      // Another website: kept exactly as typed after the host.
      address.kind = 'external';
      address.value = `${scheme}://${host}${rest}`;
      address.path = path || '/';
      address.query = query;
      address.fragment = fragment;
      if (fragment != null) address.flags.fragment = true;
      if (query) address.flags.query = true;
      if (encodedLength(address.value) > NEW_MAX) address.flags.tooLong = true;
      return address;
    }
    if (host) {
      address.tidy.push(role === 'old' ? 'host-old' : 'host-new');
      if (role === 'old' && siteHosts.size && !freeWix && !siteHosts.has(bareHost(host))) address.flags.otherDomain = true;
    }
    if (!path) path = '/';
    else if (!path.startsWith('/')) {
      path = `/${path}`;
      address.tidy.push('leading-slash');
    }
    if (/\/{2,}/.test(path)) {
      path = path.replace(/\/{2,}/g, '/');
      address.tidy.push('double-slash');
    }
    if (freeWix) {
      // username.wixsite.com/site-name/page: the site's own path starts after
      // its name.
      const segments = path.split('/').filter(Boolean);
      segments.shift();
      path = `/${segments.join('/')}`;
      address.tidy.push('free-wix');
      address.flags.freeWix = true;
    }
    if (path.length > 1 && path.endsWith('/')) {
      path = path.replace(/\/+$/, '') || '/';
      address.tidy.push('trailing-slash');
    }
    address.path = path;
    address.query = query;
    address.fragment = fragment;
    address.value = path + (query ? `?${query}` : '') + (fragment != null ? `#${fragment}` : '');
    if (fragment != null) address.flags.fragment = true;
    if (query) address.flags.query = true;
    if (role === 'new' && /(?:^|&)lang=/i.test(query)) address.flags.lang = true;
    const visible = path + query;
    if (/[\s"<>\\^`{|}]|%20/i.test(visible)) address.flags.symbols = true;
    if (/[^\x00-\x7f]/.test(visible)) address.flags.nonAscii = true;
    if (UPPER.test(path.replace(/%[0-9a-f]{2}/gi, ''))) address.flags.capitals = true;
    if (encodedLength(address.value) > (role === 'old' ? OLD_MAX : NEW_MAX)) address.flags.tooLong = true;
    return address;
  }

  // Which hosts are "this site": the one typed in "Your site address", or
  // else the one most old addresses use (a move on the same domain).
  function buildContext(rows, options = {}) {
    const siteHosts = new Set();
    const typed = siteHost(options.site);
    let inferred = '';
    if (typed) siteHosts.add(typed);
    else {
      const counts = new Map();
      rows.forEach(row => {
        const host = hostOf(row.from);
        if (host && !FREE_WIX_HOST.test(host)) counts.set(host, (counts.get(host) || 0) + 1);
      });
      let best = 0;
      counts.forEach((count, host) => {
        if (count > best) {
          best = count;
          inferred = host;
        }
      });
      if (inferred) siteHosts.add(inferred);
    }
    return { siteHosts, typed, inferred, ownAllNew: Boolean(options.ownAllNew) };
  }

  // ---- Checking a list -------------------------------------------------------------

  const LEVEL_RANK = Object.freeze({ check: 1, fix: 2, blocked: 3 });
  const ISSUE_ORDER = ['unclear', 'missing-old', 'missing-new', 'old-invalid', 'new-invalid', 'home-old', 'fragment', 'self', 'conflict', 'loop', 'lang', 'chain', 'duplicate', 'home-new', 'close-match', 'external', 'other-domain', 'free-wix-old', 'blog-target', 'query-old', 'query-new', 'fragment-new', 'case-only', 'caps', 'symbols', 'non-ascii', 'too-long', 'group-overlap'];
  const groupKey = (oldPrefix, newPrefix) => `${oldPrefix} ${newPrefix}`;
  function parseGroupKey(key) {
    const [oldPrefix, newPrefix] = text(key).split(' ');
    return oldPrefix && newPrefix ? { key: text(key), oldPrefix, newPrefix } : null;
  }

  // The folder change a row makes, when the rest of its address stays the
  // same: /products/x to /product-page/x is products → product-page.
  function prefixMove(row) {
    const { a, b } = row;
    if (a.kind !== 'path' || b.kind !== 'path' || a.query || b.query || a.fragment != null || b.fragment != null) return null;
    if (a.flags.symbols || b.flags.symbols) return null;
    const from = a.path.split('/').filter(Boolean);
    const to = b.path.split('/').filter(Boolean);
    let same = 0;
    while (same < from.length && same < to.length && from[from.length - 1 - same] === to[to.length - 1 - same]) same += 1;
    if (!same || same === from.length || same === to.length) return null;
    return { oldPrefix: from.slice(0, from.length - same).join('/'), newPrefix: to.slice(0, to.length - same).join('/') };
  }

  // Follows each redirect to where it finally lands, once per address, so a
  // 5,000-row list with long chains stays quick. A node in a cycle is a loop;
  // a node whose path reaches a cycle leads into one.
  function makeResolver(edges) {
    const resolved = new Map();
    return function resolve(start) {
      const trail = [];
      const position = new Map();
      let node = start;
      while (!resolved.has(node) && edges.has(node) && !position.has(node)) {
        position.set(node, trail.length);
        trail.push(node);
        node = edges.get(node);
      }
      let tail;
      if (resolved.has(node)) tail = resolved.get(node);
      else if (position.has(node)) {
        const cycle = trail.slice(position.get(node));
        cycle.forEach((member, at) => resolved.set(member, { inLoop: true, loop: cycle, at }));
        for (let i = position.get(node) - 1; i >= 0; i -= 1) resolved.set(trail[i], { intoLoop: true, loop: cycle, at: 0 });
        return resolved.get(start);
      } else tail = { final: node, hops: 0 };
      for (let i = trail.length - 1; i >= 0; i -= 1) {
        const next = i === trail.length - 1 ? tail : resolved.get(trail[i + 1]);
        resolved.set(trail[i], next.loop ? { intoLoop: true, loop: next.loop, at: next.at } : { final: next.final, hops: next.hops + 1 });
      }
      return resolved.get(start) || tail;
    };
  }

  // Checks a list of { from, to } rows (keys, lines and where each row came
  // from are optional) and returns every row with its status and issues,
  // the issues grouped, the counts, the rows for the file and any group
  // redirects worth suggesting.
  function analyze(inputRows, options = {}) {
    const blogPrefix = trimSlashes(options.blogPrefix == null ? DEFAULT_PREFIXES.post : options.blogPrefix).toLowerCase();
    const acceptedGroups = (options.acceptedGroups || []).map(parseGroupKey).filter(Boolean);
    const list = (inputRows || []).map((row, index) => ({
      key: row.key || `row-${index}`,
      line: row.line || index + 1,
      from: text(row.from),
      to: text(row.to),
      origin: row.origin || null,
      alternatives: row.alternatives || [],
      rule: row.rule || null,
      ruleLabel: row.ruleLabel || '',
      platformLabel: row.platformLabel || '',
      fixed: row.fixed || null,
      accepted: Boolean(row.accepted),
      added: Boolean(row.added),
      extra: row.extra || 0,
      unclear: Boolean(row.unclear),
      issues: [],
      grouped: null
    }));
    const context = buildContext(list, options);
    list.forEach(row => {
      row.a = readAddress(row.from, 'old', context);
      row.b = readAddress(row.to, 'new', context);
    });
    const add = (row, id, level, data) => {
      if (!row.issues.some(issue => issue.id === id)) row.issues.push({ id, level, ...(data || {}) });
    };
    const blocked = row => row.issues.some(issue => issue.level === 'blocked');
    // Every old address on one other domain than the site typed in is a move
    // between domains: one note says what to do, instead of the same check
    // on every row. With no site typed, the site is the commonest old host,
    // so another host is only some of the rows, and each gets the check.
    const otherHosts = new Set(list.filter(row => row.a.flags.otherDomain).map(row => bareHost(row.a.host)));
    const domainMove = otherHosts.size === 1 && !context.inferred ? [...otherHosts][0] : '';

    list.forEach(row => {
      const { a, b } = row;
      if (row.unclear) {
        add(row, 'unclear', 'blocked');
        return;
      }
      if (a.kind === 'empty') add(row, 'missing-old', 'blocked');
      else if (a.kind === 'invalid') add(row, 'old-invalid', 'blocked');
      if (b.kind === 'empty') add(row, 'missing-new', 'blocked');
      else if (b.kind === 'invalid') add(row, 'new-invalid', 'blocked');
      if (a.kind === 'path') {
        // Wix: "it is not possible to create a 301 redirect from your homepage".
        if (a.path === '/') add(row, 'home-old', 'blocked');
        // Wix: "it is not possible to use a source link with a # in the URL".
        if (a.flags.fragment) add(row, 'fragment', 'blocked');
        if (a.flags.query) add(row, 'query-old', 'check');
        if (a.flags.otherDomain && !domainMove) add(row, 'other-domain', 'check', { host: a.host });
        if (a.flags.freeWix) add(row, 'free-wix-old', 'check');
      }
      let caseOnly = false;
      if (a.kind === 'path' && b.kind === 'path' && a.path !== '/') {
        if (a.value === b.value) add(row, 'self', 'blocked', { slashOnly: a.typed !== b.typed && a.tidy.includes('trailing-slash') !== b.tidy.includes('trailing-slash') });
        else if (a.value.toLowerCase() === b.value.toLowerCase()) {
          caseOnly = true;
          add(row, 'case-only', 'check');
        }
      }
      if (b.kind === 'path') {
        // Wix: "The new URL cannot contain the language query parameter: ?lang=".
        if (b.flags.lang) add(row, 'lang', 'blocked');
        if (b.path === '/') add(row, 'home-new', 'check');
        else if (blogPrefix && b.path.toLowerCase().startsWith(`/${blogPrefix}/`)) add(row, 'blog-target', 'check');
        if (b.flags.query && !b.flags.lang) add(row, 'query-new', 'check');
        if (b.flags.fragment) add(row, 'fragment-new', 'check');
      }
      if (b.kind === 'external') {
        add(row, 'external', 'check', { host: b.host });
        if (b.flags.fragment) add(row, 'fragment-new', 'check');
      }
      const sides = which => [a, b].filter(address => address.kind === 'path' && address.flags[which]).map(address => address.role);
      const capitals = sides('capitals');
      if (capitals.length && !caseOnly) add(row, 'caps', 'check', { sides: capitals });
      const symbols = sides('symbols');
      if (symbols.length) add(row, 'symbols', 'check', { sides: symbols });
      const nonAscii = sides('nonAscii');
      if (nonAscii.length && !symbols.length) add(row, 'non-ascii', 'check', { sides: nonAscii });
      const long = [a, b].filter(address => address.flags.tooLong).map(address => address.role);
      if (long.length) add(row, 'too-long', 'check', { sides: long });
      if (['close', 'words', 'ambiguous', 'folder'].includes(row.origin) && !row.accepted) add(row, 'close-match', 'check', { kind: row.origin });
    });

    // One old address, several rows: the same new address is a duplicate,
    // different ones are a conflict that only the owner can settle.
    const valid = row => !blocked(row) && row.a.kind === 'path' && (row.b.kind === 'path' || row.b.kind === 'external');
    const byFrom = new Map();
    list.forEach(row => {
      if (!valid(row)) return;
      const rows = byFrom.get(row.a.value);
      if (rows) rows.push(row);
      else byFrom.set(row.a.value, [row]);
    });
    byFrom.forEach(rows => {
      if (rows.length < 2) return;
      const targets = new Set(rows.map(row => row.b.value));
      if (targets.size === 1) rows.slice(1).forEach(row => add(row, 'duplicate', 'fix', { of: rows[0].line, firstKey: rows[0].key }));
      else {
        rows.forEach(row => add(row, 'conflict', 'blocked', {
          others: rows.filter(other => other.b.value !== row.b.value).map(other => ({ line: other.line, to: other.b.value }))
        }));
      }
    });

    // Chains and loops: follow each redirect through the others.
    const edges = new Map();
    list.forEach(row => {
      if (valid(row)) edges.set(row.a.value, row.b.value);
    });
    const resolve = makeResolver(edges);
    list.forEach(row => {
      if (!valid(row)) return;
      const end = resolve(row.a.value);
      if (!end) return;
      if (end.loop) {
        // The loop as it reads from this row (or from where the row enters
        // it), shortened for display when it is long.
        const size = end.loop.length;
        const step = offset => end.loop[(end.at + offset) % size];
        const cycle = size <= 5 ? Array.from({ length: size + 1 }, (_, offset) => step(offset)) : [step(0), step(1), step(2), '…', step(size - 1), step(0)];
        add(row, 'loop', 'blocked', { cycle, size, into: Boolean(end.intoLoop) });
      } else if (end.hops >= 2) {
        const steps = [row.a.value];
        let node = row.a.value;
        for (let step = 0; step < end.hops && step < 60; step += 1) {
          node = edges.get(node);
          steps.push(node);
        }
        add(row, 'chain', 'fix', { steps, final: end.final, hops: end.hops });
      }
    });

    // Rows covered by a group redirect the owner chose leave the file; other
    // rows in that old folder are flagged, since Wix says a group can't use
    // a path another redirect already uses.
    if (acceptedGroups.length) {
      list.forEach(row => {
        if (blocked(row) || row.issues.some(issue => issue.level === 'fix')) return;
        const move = prefixMove(row);
        const match = move && acceptedGroups.find(group => group.key === groupKey(move.oldPrefix, move.newPrefix));
        if (match) row.grouped = match.key;
      });
      list.forEach(row => {
        if (row.grouped || row.a.kind !== 'path') return;
        const group = acceptedGroups.find(item => row.a.path.startsWith(`/${item.oldPrefix}/`));
        if (group) add(row, 'group-overlap', 'check', { oldPrefix: group.oldPrefix });
      });
    }

    list.forEach(row => {
      const top = row.issues.reduce((max, issue) => Math.max(max, LEVEL_RANK[issue.level] || 0), 0);
      row.issues.sort((x, y) => LEVEL_RANK[y.level] - LEVEL_RANK[x.level] || ISSUE_ORDER.indexOf(x.id) - ISSUE_ORDER.indexOf(y.id));
      row.status = top === 3 ? 'blocked' : top === 2 ? 'fix' : top === 1 ? 'check' : row.fixed ? 'fixed' : row.origin === 'pattern' ? 'suggested' : 'ready';
    });

    const rows = list.filter(row => !row.grouped);
    const has = id => row => row.issues.some(issue => issue.id === id);
    // Blocked rows stay out of the file; a repeated row is written once.
    const exportRows = rows.filter(row => row.status !== 'blocked' && !has('duplicate')(row)).map(row => ({ from: row.a.value, to: row.b.value }));
    const counts = { total: rows.length, ready: 0, fixed: 0, suggested: 0, check: 0, fix: 0, blocked: 0 };
    rows.forEach(row => { counts[row.status] += 1; });
    counts.exported = exportRows.length;
    counts.duplicatesLeft = rows.filter(has('duplicate')).length;
    counts.chainsLeft = rows.filter(row => row.status !== 'blocked' && !has('duplicate')(row) && has('chain')(row)).length;
    counts.attention = counts.check + counts.fix + counts.blocked;
    counts.grouped = list.length - rows.length;
    counts.chainsFixed = rows.filter(row => row.fixed && row.fixed.kind === 'chain' && row.status !== 'blocked').length;
    counts.home = rows.filter(row => row.issues.some(issue => issue.id === 'home-new')).length;

    const issueMap = new Map();
    rows.forEach(row => row.issues.forEach(issue => {
      const entry = issueMap.get(issue.id) || issueMap.set(issue.id, { id: issue.id, level: issue.level, rows: [], keys: new Set(), olds: new Set() }).get(issue.id);
      entry.rows.push(row);
      entry.olds.add(row.a.value);
    }));
    const issues = [...issueMap.values()].map(entry => ({
      id: entry.id,
      level: entry.level,
      rows: entry.rows,
      count: entry.id === 'conflict' ? entry.olds.size : entry.rows.length,
      lines: entry.rows.map(row => row.line)
    })).sort((x, y) => LEVEL_RANK[y.level] - LEVEL_RANK[x.level] || ISSUE_ORDER.indexOf(x.id) - ISSUE_ORDER.indexOf(y.id));

    const tidy = { 'host-old': 0, 'host-new': 0, 'trailing-slash': 0, 'double-slash': 0, 'leading-slash': 0, 'empty-query': 0, 'free-wix': 0 };
    rows.forEach(row => [row.a, row.b].forEach(address => address.tidy.forEach(id => {
      if (id in tidy) tidy[id] += 1;
    })));

    // A host on two or more new addresses, with no site address typed, may
    // well be the owner's own Wix site.
    let siteSuggestion = '';
    if (!context.typed) {
      const externals = new Map();
      rows.forEach(row => {
        if (row.b.kind === 'external' && !FREE_WIX_HOST.test(row.b.host)) {
          const host = bareHost(row.b.host);
          externals.set(host, (externals.get(host) || 0) + 1);
        }
      });
      let best = 1;
      externals.forEach((count, host) => {
        if (count > best) {
          best = count;
          siteSuggestion = host;
        }
      });
    }

    const result = {
      rows,
      all: list,
      issues,
      counts,
      exportRows,
      files: splitFiles(exportRows),
      tidy,
      context: { site: context.typed, inferred: context.inferred, hostsConverted: tidy['host-new'] },
      siteSuggestion,
      domainMove,
      overCap: exportRows.length > PER_SITE,
      groups: findGroups(rows, acceptedGroups),
      acceptedGroups: acceptedGroups.map(group => ({ ...group, count: list.filter(row => row.grouped === group.key).length }))
    };
    return result;
  }

  // Folder changes that one group redirect could make instead of many rows.
  // Wix's own example of a group redirect is products → product-page.
  function findGroups(rows, acceptedGroups = []) {
    const accepted = new Set(acceptedGroups.map(group => group.key));
    const moves = new Map();
    rows.forEach(row => {
      if (row.status === 'blocked' || row.status === 'fix') return;
      const move = prefixMove(row);
      if (!move) return;
      const key = groupKey(move.oldPrefix, move.newPrefix);
      const entry = moves.get(key) || moves.set(key, { key, oldPrefix: move.oldPrefix, newPrefix: move.newPrefix, rows: [] }).get(key);
      entry.rows.push(row);
    });
    // How many rows sit under each folder, counted once for the whole list.
    // Rows with ?… are the same pages again, and Wix doesn't say how a group
    // treats the part after ?, so they don't count against the share; nor do
    // blocked rows, which aren't in the file anyway.
    const DEPTH = 12;
    const family = new Map();
    const inFamily = row => row.status !== 'blocked' && row.a.kind === 'path' && !row.a.query;
    rows.forEach(row => {
      if (!inFamily(row)) return;
      const segments = row.a.path.split('/').filter(Boolean);
      let folder = '';
      for (let index = 0; index < Math.min(segments.length - 1, DEPTH); index += 1) {
        folder = folder ? `${folder}/${segments[index]}` : segments[index];
        family.set(folder, (family.get(folder) || 0) + 1);
      }
    });
    const familyOf = folder => (folder.split('/').length <= DEPTH ? family.get(folder) || 0 : rows.filter(row => inFamily(row) && row.a.path.startsWith(`/${folder}/`)).length);
    const groups = [];
    moves.forEach(entry => {
      if (accepted.has(entry.key) || entry.rows.length < GROUP_MIN) return;
      const familySize = familyOf(entry.oldPrefix);
      if (entry.rows.length / familySize < GROUP_SHARE) return;
      groups.push({ key: entry.key, oldPrefix: entry.oldPrefix, newPrefix: entry.newPrefix, count: entry.rows.length, family: familySize, exceptions: familySize - entry.rows.length, examples: entry.rows.slice(0, 2).map(row => ({ from: row.a.value, to: row.b.value })) });
    });
    return groups.sort((x, y) => y.count - x.count || x.key.localeCompare(y.key));
  }

  // Points every chain straight at its final address. Loops are left alone:
  // there is no right final address to choose.
  function flattenChains(rows, options = {}) {
    const result = analyze(rows, options);
    const finals = new Map(result.all.map(row => [row.key, row.issues.find(issue => issue.id === 'chain')]));
    return (rows || []).map((row, index) => {
      const chain = finals.get(row.key || `row-${index}`);
      return chain ? { ...row, to: chain.final, fixed: { kind: 'chain', before: text(row.to) } } : { ...row };
    });
  }

  // ---- The file -------------------------------------------------------------------

  // Two columns, old then new, under a header row (Wix: "the first row should
  // contain column headers ... just two columns"). UTF-8 with plain line
  // breaks; no byte-order mark.
  function buildCsv(rows) {
    return toCsv([CSV_HEADER.slice()].concat((rows || []).map(row => [row.from, row.to])), { eol: '\n' });
  }

  function splitFiles(rows, perFile = PER_FILE) {
    const list = rows || [];
    if (!list.length) return [];
    const size = Math.max(1, perFile);
    const count = Math.ceil(list.length / size);
    return Array.from({ length: count }, (_, index) => {
      const part = list.slice(index * size, (index + 1) * size);
      return {
        name: count === 1 ? 'wix-redirects.csv' : `wix-redirects-${index + 1}-of-${count}.csv`,
        index,
        count,
        first: index * size + 1,
        last: index * size + part.length,
        rows: part,
        csv: buildCsv(part)
      };
    });
  }

  // ---- Matching two lists by name -------------------------------------------------

  const STOP_WORDS = new Set(['a', 'an', 'and', 'the', 'of', 'for', 'with', 'in', 'on', 'to', 'by', 'at', 'or']);
  const decodeXml = value => text(value).replace(/^<!\[CDATA\[|\]\]>$/g, '').replace(/&(amp|lt|gt|quot|apos|#39);/g, (_, entity) => ({ amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'" })[entity]);

  // Addresses from pasted lines, a crawl export or a sitemap file's text
  // (its <loc> entries; image entries are not pages).
  function extractAddresses(input) {
    if (Array.isArray(input)) return { items: input.map((value, index) => ({ text: text(value), line: index + 1 })), skipped: 0 };
    const source = text(input).replace(/^﻿/, '');
    const items = [];
    let skipped = 0;
    // A sitemap index (Shopify's and Wix's sitemap.xml are both indexes)
    // lists other sitemaps, not pages.
    if (/<sitemapindex/i.test(source)) return { items, skipped, sitemap: true, index: true };
    if (/<loc>/i.test(source)) {
      // Found by position, not one lazy pattern, so a <loc> with no end
      // can't make the browser scan the rest of the text again and again.
      const open = /<loc>/gi;
      const close = /<\/loc>/gi;
      for (let start = open.exec(source); start && items.length < MAX_LINES; start = open.exec(source)) {
        close.lastIndex = open.lastIndex;
        const end = close.exec(source);
        if (!end) break;
        items.push({ text: decodeXml(source.slice(open.lastIndex, end.index).trim()).trim(), line: items.length + 1 });
        open.lastIndex = close.lastIndex;
      }
      return { items, skipped, sitemap: true };
    }
    source.split(/\r\n|\n|\r/).slice(0, MAX_LINES).forEach((line, index) => {
      const value = tidyText(line);
      if (!value) return;
      const token = value.split(/[\s,;"'<>]+/).find(part => part && looksLikeAddress(part));
      if (token) items.push({ text: token, line: index + 1 });
      else skipped += 1;
    });
    return { items, skipped };
  }

  // The last part of a path, which is the page's name on every platform here.
  // Accented letters are compared in one form (NFC), so "café" typed with a
  // separate accent mark still matches.
  function slugOf(path) {
    const clean = trimSlashes(text(path).split(/[?#]/)[0]);
    const last = clean.slice(clean.lastIndexOf('/') + 1);
    return safeDecode(last).normalize('NFC').toLowerCase().replace(/\.(?:s?html?|php\d?|aspx?|jsp|cfm)$/, '');
  }
  const stem = word => (word.length > 3 && /[^s]s$/.test(word) ? word.slice(0, -1) : word);
  const wordsOf = slug => [...new Set(slug.split(WORD_SPLIT).filter(word => word && !STOP_WORDS.has(word)).map(stem))];
  // "copy-of-x", "x-copy" and "x-2" are how stores name a duplicated page.
  function looseOf(slug) {
    let base = slug.replace(/^copy-of-/, '').replace(/-copy$/, '');
    let numbered = '';
    const match = /^(.*\D)-(\d{1,2})$/.exec(base);
    if (match) {
      base = match[1];
      numbered = match[2];
    }
    return { key: base.replace(NOT_WORD, ''), numbered };
  }

  // What kind of page an address is, by the platforms' address patterns.
  function pageTypeOf(path, prefixes = DEFAULT_PREFIXES) {
    const value = text(path).split(/[?#]/)[0].toLowerCase();
    const segments = value.split('/').filter(Boolean);
    const starts = prefix => Boolean(prefix) && value.startsWith(`/${trimSlashes(prefix).toLowerCase()}/`);
    if (/^\/products\/[^/]+$/.test(value) || /^\/collections\/[^/]+\/products\/[^/]+$/.test(value) || /^\/product\/.+/.test(value) || /^\/[^/]+\/p\/[^/]+$/.test(value) || starts(prefixes.product)) return 'product';
    if (/^\/collections\/[^/]+$/.test(value) || /^\/product-category\/.+/.test(value) || starts(prefixes.category)) return 'category';
    if (/^\/blogs\/[^/]+\/[^/]+$/.test(value) || starts(prefixes.post) || /^\/\d{4}\/\d{1,2}\/(?:\d{1,2}\/)?[^/]+$/.test(value) || /^\/blog\/.+/.test(value)) return 'post';
    if (/^\/pages\/[^/]+$/.test(value) || segments.length === 1) return 'page';
    return 'other';
  }
  const fit = (oldType, newType) => (oldType === 'other' || newType === 'other' ? 1 : oldType === newType ? 2 : 0);

  function indexNew(values, prefixes) {
    const entries = values.map((value, index) => {
      const slug = slugOf(value);
      const loose = looseOf(slug);
      const words = wordsOf(slug);
      return { index, value, slug, loose: loose.key, numbered: loose.numbered, words, wordSet: new Set(words), type: pageTypeOf(value, prefixes) };
    });
    const exact = new Map();
    const loose = new Map();
    const postings = new Map();
    const rarest = new Map();
    const push = (map, key, entry) => {
      const list = map.get(key);
      if (list) list.push(entry);
      else map.set(key, [entry]);
    };
    entries.forEach(entry => {
      if (!entry.slug) return;
      push(exact, entry.slug, entry);
      if (entry.loose) push(loose, entry.loose, entry);
      entry.words.forEach(word => push(postings, word, entry));
    });
    // Each new name filed under its rarest word: an old name can only
    // contain the new one if it has that word too.
    entries.forEach(entry => {
      let best = null;
      entry.words.forEach(word => {
        const size = postings.get(word).length;
        if (!best || size < best.size || (size === best.size && word < best.word)) best = { word, size };
      });
      if (best) push(rarest, best.word, entry);
    });
    return { entries, exact, loose, postings, rarest };
  }

  // The best new address for one old address, or null. Same name → "exact";
  // the same once duplicate endings and punctuation are ignored → "close";
  // all the words of one name inside the other → "words". Ties between
  // several new addresses → "ambiguous". Only "exact" is presented as a match.
  function bestMatch(old, index, prefixes) {
    const slug = slugOf(old.path);
    if (!slug) return null;
    const type = pageTypeOf(old.path, prefixes);
    const order = (x, y) => fit(type, y.type) - fit(type, x.type) || x.value.length - y.value.length || (x.value < y.value ? -1 : x.value > y.value ? 1 : 0);
    const pack = (origin, list) => ({ to: list[0].value, origin, alternatives: list.slice(1, 6).map(entry => entry.value), others: list.length - 1 });
    const exact = index.exact.get(slug);
    if (exact) {
      const sorted = exact.slice().sort(order);
      const best = fit(type, sorted[0].type);
      const tied = sorted.filter(entry => fit(type, entry.type) === best);
      if (best === 0) return pack('ambiguous', sorted);
      // An old folder no platform here uses (/shop/linen, /journal/linen)
      // says nothing about the kind of page: the same name is a Check.
      if (type === 'other') return pack('folder', sorted);
      return pack(tied.length === 1 ? 'exact' : 'ambiguous', tied.concat(sorted.filter(entry => !tied.includes(entry))));
    }
    const loose = looseOf(slug);
    const near = (loose.key && index.loose.get(loose.key) || []).filter(entry => !(loose.numbered && entry.numbered && loose.numbered !== entry.numbered));
    if (near.length) return pack('close', near.slice().sort(order));
    const words = wordsOf(slug);
    if (!words.length) return null;
    const mine = new Set(words);
    const found = new Map();
    let rare = null;
    words.forEach(word => {
      const size = (index.postings.get(word) || []).length;
      if (rare === null || size < rare.size) rare = { word, size };
    });
    // Old name inside a new one: scan the new names that share its rarest word.
    if (rare && rare.size && rare.size <= 2000) {
      index.postings.get(rare.word).forEach(entry => {
        if (words.every(word => entry.wordSet.has(word))) found.set(entry.index, entry);
      });
    }
    // New name inside the old one.
    words.forEach(word => (index.rarest.get(word) || []).forEach(entry => {
      if (entry.words.every(item => mine.has(item))) found.set(entry.index, entry);
    }));
    if (!found.size) return null;
    const scored = [...found.values()].map(entry => {
      const small = Math.min(words.length, entry.words.length);
      const large = Math.max(words.length, entry.words.length);
      return { entry, score: small / large };
    }).filter(item => item.score >= 0.4);
    if (!scored.length) return null;
    scored.sort((x, y) => fit(type, y.entry.type) - fit(type, x.entry.type) || y.score - x.score || order(x.entry, y.entry));
    const top = scored[0];
    const tied = scored.filter(item => fit(type, item.entry.type) === fit(type, top.entry.type) && item.score === top.score);
    if (tied.length > 10) return null;
    return pack(tied.length > 1 ? 'ambiguous' : 'words', scored.map(item => item.entry));
  }

  // Pairs each old address with a new Wix address by name. The new list is
  // the Wix site's own addresses, so any domain on them (a free Wix address
  // before the domain moves, too) is reduced to the path.
  function matchLists(oldInput, newInput, options = {}) {
    const prefixes = { ...DEFAULT_PREFIXES, ...(options.prefixes || {}) };
    const oldItems = extractAddresses(oldInput);
    const newItems = extractAddresses(newInput);
    const skipped = { home: 0, homeQuery: 0, homeQueryExample: '', repeated: 0, notAddress: oldItems.skipped, invalid: 0 };
    const seenOld = new Set();
    const olds = [];
    oldItems.items.forEach(item => {
      const address = readAddress(item.text, 'old', {});
      if (address.kind !== 'path') {
        skipped.invalid += 1;
        return;
      }
      if (address.path === '/') {
        countHome(skipped, address);
        return;
      }
      if (seenOld.has(address.value)) {
        skipped.repeated += 1;
        return;
      }
      seenOld.add(address.value);
      olds.push({ value: address.value, path: address.path, line: item.line });
    });
    const seenNew = new Set();
    const news = [];
    let freeWix = 0;
    newItems.items.forEach(item => {
      const address = readAddress(item.text, 'new', { ownAllNew: true });
      if (address.kind !== 'path' || seenNew.has(address.value)) return;
      if (address.flags.freeWix) freeWix += 1;
      seenNew.add(address.value);
      news.push(address.value);
    });
    const index = indexNew(news.filter(value => value !== '/'), prefixes);
    const pairs = [];
    const unmatched = [];
    const counts = { exact: 0, close: 0 };
    olds.forEach(old => {
      const key = `o${JSON.stringify(old.value)}`;
      const match = bestMatch(old, index, prefixes);
      if (!match) {
        unmatched.push({ key, line: old.line, from: old.value });
        return;
      }
      if (match.origin === 'exact') counts.exact += 1;
      else counts.close += 1;
      pairs.push({ key, line: old.line, from: old.value, to: match.to, origin: match.origin, alternatives: match.alternatives, others: match.others });
    });
    return { pairs, unmatched, options: news, skipped, counts, freeWix, oldCount: olds.length, newCount: news.length, oldIndex: Boolean(oldItems.index), newIndex: Boolean(newItems.index) };
  }

  // An old address at the root: the home page itself, or one like /?p=12
  // that only has a ?… part. Neither can be an old address in Wix.
  function countHome(skipped, address) {
    if (!address.query) {
      skipped.home += 1;
      return;
    }
    skipped.homeQuery += 1;
    if (!skipped.homeQueryExample) skipped.homeQueryExample = address.value;
  }

  // ---- Same names, new folders --------------------------------------------------

  // Each platform's address patterns, as data. "from" is a list of path
  // segments: a word is literal, {slug} is the page's name, *x is any one
  // segment (shown as x), ** is any number of segments, #yyyy/#mm/#dd are
  // numbers, {opt:x} is a folder the owner can change. "to" names the Wix
  // folder ('' for a plain /name page). Only patterns from the platforms'
  // own documentation are here; BigCommerce publishes none, so it has no
  // preset (the other two ways of building a list work for it).
  const PLATFORMS = Object.freeze({
    shopify: Object.freeze({
      label: 'Shopify',
      defaults: Object.freeze({ locale: '' }),
      rules: Object.freeze([
        { id: 'product-in-collection', label: 'Products shown in a collection', from: ['collections', '*collection', 'products', '{slug}'], to: 'product' },
        { id: 'product', label: 'Products', from: ['products', '{slug}'], to: 'product' },
        { id: 'collection', label: 'Collections', from: ['collections', '{slug}'], to: 'category' },
        { id: 'page', label: 'Pages', from: ['pages', '{slug}'], to: '' },
        { id: 'blog', label: 'Blog posts', from: ['blogs', '*blog', '{slug}'], to: 'post' }
      ])
    }),
    woocommerce: Object.freeze({
      label: 'WooCommerce',
      defaults: Object.freeze({ product: 'product', category: 'product-category', postName: false, blog: '' }),
      rules: Object.freeze([
        { id: 'product', label: 'Products', from: ['{opt:product}', '**', '{slug}'], to: 'product' },
        { id: 'category', label: 'Product categories', from: ['{opt:category}', '**', '{slug}'], to: 'category' },
        { id: 'post-base', label: 'Blog posts', when: 'postName', from: ['{opt:blog}', '**', '{slug}'], to: 'post' },
        { id: 'post-day', label: 'Blog posts with a date', when: 'postName', from: ['#yyyy', '#mm', '#dd', '{slug}'], to: 'post' },
        { id: 'post-month', label: 'Blog posts with a month', when: 'postName', from: ['#yyyy', '#mm', '{slug}'], to: 'post' },
        { id: 'page', label: 'Pages', from: ['{slug}'], to: '' }
      ])
    }),
    squarespace: Object.freeze({
      label: 'Squarespace',
      defaults: Object.freeze({ store: 'shop', blog: 'blog' }),
      rules: Object.freeze([
        { id: 'product', label: 'Products', from: ['{opt:store}', 'p', '{slug}'], to: 'product' },
        { id: 'post', label: 'Blog posts', from: ['{opt:blog}', '**', '{slug}'], not: /^\/[^/]+\/(?:category|tag)\//i, to: 'post' },
        { id: 'page', label: 'Pages', from: ['{slug}'], to: '' }
      ])
    }),
    wix: Object.freeze({
      label: 'Wix',
      defaults: Object.freeze({ product: 'product-page', post: 'post' }),
      rules: Object.freeze([
        { id: 'product', label: 'Product pages', from: ['{opt:product}', '{slug}'], to: 'product' },
        { id: 'post', label: 'Blog posts', from: ['{opt:post}', '{slug}'], to: 'post' }
      ])
    })
  });
  const PLATFORM_IDS = Object.freeze(Object.keys(PLATFORMS));
  const splitFolders = value => trimSlashes(value).split('/').filter(Boolean);

  function settingsFor(platform, settings = {}) {
    const preset = PLATFORMS[platform] || PLATFORMS.shopify;
    const out = { ...preset.defaults };
    Object.keys(preset.defaults).forEach(key => {
      if (settings[key] !== undefined) out[key] = typeof preset.defaults[key] === 'boolean' ? Boolean(settings[key]) : text(settings[key]);
    });
    return out;
  }

  // The rules that apply with these settings: a rule whose folder is empty
  // would match everything, so it is left out.
  function activeRules(platform, settings) {
    const preset = PLATFORMS[platform] || PLATFORMS.shopify;
    return preset.rules.filter(rule => {
      if (rule.when && !settings[rule.when]) return false;
      return rule.from.every(token => !token.startsWith('{opt:') || splitFolders(settings[token.slice(5, -1)]).length);
    });
  }

  function matchRule(segments, rule, settings) {
    const tokens = [];
    rule.from.forEach(token => {
      if (token.startsWith('{opt:')) splitFolders(settings[token.slice(5, -1)]).forEach(part => tokens.push({ literal: part.toLowerCase() }));
      else if (token === '{slug}') tokens.push({ slug: true });
      else if (token === '**') tokens.push({ many: true });
      else if (token.startsWith('*')) tokens.push({ any: true });
      else if (token.startsWith('#')) tokens.push({ digits: true });
      else tokens.push({ literal: token });
    });
    const walk = (i, j) => {
      if (j === tokens.length) return i === segments.length ? {} : null;
      const token = tokens[j];
      if (token.many) {
        for (let k = i; k <= segments.length; k += 1) {
          const found = walk(k, j + 1);
          if (found) return found;
        }
        return null;
      }
      if (i >= segments.length) return null;
      const segment = segments[i];
      if (token.literal) return segment.toLowerCase() === token.literal ? walk(i + 1, j + 1) : null;
      if (token.digits) return /^\d+$/.test(segment) ? walk(i + 1, j + 1) : null;
      const found = walk(i + 1, j + 1);
      if (!found) return null;
      return token.slug ? { ...found, slug: segment } : found;
    };
    return walk(0, 0);
  }

  const buildTarget = (folder, slug) => `/${splitFolders(folder).concat(slug).join('/')}`;

  // Suggests a Wix address for each old address by swapping the platform's
  // folder for Wix's and keeping the name. Addresses that would stay the same
  // need no redirect; ones no pattern fits are listed for a destination.
  function suggestFromPattern(oldInput, platform, prefixes, options = {}) {
    const id = PLATFORMS[platform] ? platform : 'shopify';
    const preset = PLATFORMS[id];
    const wix = { ...DEFAULT_PREFIXES, ...(prefixes || {}) };
    const settings = settingsFor(id, options);
    const rules = activeRules(id, settings);
    const folders = id === 'shopify' ? text(settings.locale).toLowerCase().split(/[\s,/]+/).filter(Boolean) : [];
    const items = extractAddresses(oldInput);
    const pairs = [];
    const unmatched = [];
    const same = [];
    const skipped = { home: 0, homeQuery: 0, homeQueryExample: '', repeated: 0, invalid: 0, notAddress: items.skipped };
    const seen = new Set();
    items.items.forEach(item => {
      const address = readAddress(item.text, 'old', {});
      if (address.kind !== 'path') {
        skipped.invalid += 1;
        return;
      }
      if (address.path === '/') {
        // WordPress's plain addresses (/?p=123) start at the home page too.
        countHome(skipped, address);
        return;
      }
      if (seen.has(address.value)) {
        skipped.repeated += 1;
        return;
      }
      seen.add(address.value);
      const key = `o${JSON.stringify(address.value)}`;
      let segments = address.path.split('/').filter(Boolean);
      if (folders.length && folders.includes(text(segments[0]).toLowerCase())) segments = segments.slice(1);
      let to = '';
      let used = null;
      for (const rule of rules) {
        if (rule.not && rule.not.test(address.path)) continue;
        const found = matchRule(segments, rule, settings);
        if (found && found.slug) {
          to = buildTarget(wix[rule.to] || '', found.slug);
          used = rule;
          break;
        }
      }
      if (!used) unmatched.push({ key, line: item.line, from: address.value });
      else if (to.toLowerCase() === address.path.toLowerCase()) same.push({ line: item.line, from: address.value });
      else pairs.push({ key, line: item.line, from: address.value, to, origin: 'pattern', rule: used.id, ruleLabel: used.label, platformLabel: preset.label });
    });
    return { pairs, unmatched, same, skipped, platform: id, settings, rules, index: Boolean(items.index) };
  }

  // ---- From the page's state to a result -------------------------------------------

  function normalizeState(input = {}) {
    const mode = ['pairs', 'match', 'pattern'].includes(input.mode) ? input.mode : 'pairs';
    const platform = PLATFORMS[input.platform] ? input.platform : 'shopify';
    const prefixes = { ...DEFAULT_PREFIXES };
    Object.keys(DEFAULT_PREFIXES).forEach(key => {
      if (input.prefixes && input.prefixes[key] != null) prefixes[key] = trimSlashes(input.prefixes[key]);
    });
    return {
      mode,
      site: text(input.site),
      pairs: text(input.pairs),
      old: text(input.old),
      new: text(input.new),
      pattern: text(input.pattern),
      platform,
      settings: settingsFor(platform, (input.settings && input.settings[platform]) || {}),
      prefixes,
      overrides: input.overrides || {},
      added: input.added || [],
      groups: input.groups || [],
      keepHeader: Boolean(input.keepHeader)
    };
  }

  // Pairs get keys from their own text, so a fix on one line survives edits
  // to the others.
  function pairsSource(value, keepHeader) {
    const parsed = parseInput(value, { header: !keepHeader });
    const seen = new Map();
    const rows = parsed.rows.map(row => {
      const id = `${row.from}\n${row.to}`;
      const repeat = seen.get(id) || 0;
      seen.set(id, repeat + 1);
      return { ...row, key: `p${JSON.stringify([row.from, row.to, repeat])}` };
    });
    return { rows, parsed };
  }

  // The owner's taps (edits, fixes, removals) sit on top of the rows the
  // list produced, keyed by row.
  function applyOverrides(rows, overrides = {}, added = []) {
    const out = [];
    rows.concat(added.map(row => ({ ...row, added: true }))).forEach(row => {
      const change = overrides[row.key];
      if (change && change.removed) return;
      const next = { ...row };
      if (change) {
        if (change.from != null) next.from = change.from;
        if (change.to != null) {
          next.to = change.to;
          if (!change.fixed) next.origin = 'manual';
        }
        if (change.fixed) next.fixed = change.fixed;
        if (change.accepted) next.accepted = true;
      }
      out.push(next);
    });
    return out;
  }

  function cached(memo, name, parts, run) {
    if (!memo) return run();
    const id = JSON.stringify(parts);
    if (memo[name] && memo[name].id === id) return memo[name].value;
    const value = run();
    memo[name] = { id, value };
    return value;
  }

  function compute(input = {}, memo) {
    const state = normalizeState(input);
    let rows = [];
    let unmatched = [];
    const source = { mode: state.mode };
    if (state.mode === 'pairs') {
      const { rows: list, parsed } = cached(memo, 'pairs', [state.pairs, state.keepHeader], () => pairsSource(state.pairs, state.keepHeader));
      rows = applyOverrides(list, state.overrides, state.added);
      source.header = parsed.header;
      source.truncated = parsed.truncated;
      source.comments = parsed.comments;
      source.sitemap = parsed.sitemap;
      source.extra = list.filter(row => row.extra).length;
      source.empty = !parsed.rows.length && !state.added.length;
    } else {
      const made = state.mode === 'match'
        ? cached(memo, 'match', [state.old, state.new, state.prefixes], () => matchLists(state.old, state.new, { prefixes: state.prefixes }))
        : cached(memo, 'pattern', [state.pattern, state.platform, state.settings, state.prefixes], () => suggestFromPattern(state.pattern, state.platform, state.prefixes, state.settings));
      const waiting = made.unmatched.map(item => ({ ...item, to: '', origin: 'unmatched' }));
      applyOverrides(made.pairs.concat(waiting), state.overrides, state.added).forEach(row => {
        if (row.origin === 'unmatched' && !text(row.to).trim()) unmatched.push(row);
        else rows.push(row);
      });
      source.made = made;
      source.empty = state.mode === 'match' ? !made.oldCount && !state.added.length : !made.pairs.length && !made.unmatched.length && !made.same.length && !state.added.length;
      source.waitingForNew = state.mode === 'match' && made.oldCount > 0 && !made.newCount && !made.newIndex;
    }
    const result = analyze(rows, { site: state.site, blogPrefix: state.prefixes.post, acceptedGroups: state.groups, ownAllNew: state.mode === 'match' });
    result.mode = state.mode;
    result.source = source;
    result.unmatched = unmatched;
    result.platform = state.platform;
    result.summary = summaryText(result);
    result.next = nextText(result);
    return result;
  }

  // What the first paint shows: the made-up pairs, checked.
  function describe(sample = SAMPLE) {
    return compute({ mode: 'pairs', pairs: sample });
  }

  // A still for the /tools directory card: four rows of the worked example
  // (a ready row, the chain, the home-page row and a conflict) exactly as the
  // tool shows them, and its summary line.
  function directoryPreview() {
    const result = describe(SAMPLE);
    const pick = test => result.rows.find(test);
    const has = id => row => row.issues.some(issue => issue.id === id);
    const chosen = [pick(row => row.status === 'ready'), pick(has('chain')), pick(has('home-new')), pick(has('conflict'))].filter(Boolean);
    return {
      rows: chosen.map(row => ({ from: row.a.value, to: row.b.value, status: row.status })),
      summary: result.summary
    };
  }

  // ---- Words on the page ------------------------------------------------------------

  const STATUS_WORDS = Object.freeze({ ready: 'Ready', fixed: 'Fixed', suggested: 'Suggested', check: 'Check', fix: 'Fix', blocked: 'Blocked' });
  const counted = (count, one, many) => (count === 1 ? one : many);

  // The summary line, as parts: "148 redirects ready · 6 chains flattened ·
  // 3 need your decision". Each part has a kind for its marker on the page.
  function summaryItems(result) {
    const { counts } = result;
    const waiting = (result.unmatched || []).length;
    if (!counts.total && !waiting && !counts.grouped) return [];
    const items = [];
    const ready = counts.ready + counts.fixed;
    if (ready || !counts.suggested) items.push({ kind: 'ready', text: `${plural(ready, 'redirect')} ready` });
    if (counts.chainsFixed) items.push({ kind: 'fixed', text: `${plural(counts.chainsFixed, 'chain')} flattened` });
    if (counts.suggested) items.push({ kind: 'suggested', text: `${number(counts.suggested)} suggested` });
    if (counts.grouped) items.push({ kind: 'group', text: `${number(counts.grouped)} in a group redirect` });
    if (counts.attention) items.push({ kind: 'decision', text: `${number(counts.attention)} ${counted(counts.attention, 'needs', 'need')} your decision` });
    if (waiting) items.push({ kind: 'waiting', text: `${number(waiting)} ${counted(waiting, 'needs', 'need')} a destination` });
    return items;
  }
  const summaryParts = result => summaryItems(result).map(item => item.text);
  const summaryText = result => summaryParts(result).join(' · ');

  const EMPTY = Object.freeze({
    pairs: 'Paste your list of old and new addresses to start.',
    match: 'Paste your old addresses and your new Wix addresses to match them.',
    pattern: 'Paste your old addresses to see the Wix address for each.'
  });

  // What the box a list was pasted into says about it, when it isn't a list
  // the tool can use: a sitemap in the pairs box, or a sitemap index.
  const NOTES = Object.freeze({
    sitemap: 'This looks like a sitemap. Use “Match two lists” or “Same names” for it.',
    index: 'This is a sitemap index: it lists other sitemaps. Open each one it lists and paste its text here.',
    indexNew: 'This is a sitemap index: it lists other sitemaps. Open each one it lists (like store-products-sitemap.xml) and paste its text here.'
  });
  function inputNotes(result) {
    const source = result.source || {};
    const made = source.made || {};
    return {
      pairs: result.mode === 'pairs' && source.sitemap ? NOTES.sitemap : '',
      old: result.mode === 'match' && made.oldIndex ? NOTES.index : '',
      new: result.mode === 'match' && made.newIndex ? NOTES.indexNew : '',
      pattern: result.mode === 'pattern' && made.index ? NOTES.index : ''
    };
  }

  function nextText(result) {
    const { counts } = result;
    const source = result.source || {};
    if (result.overCap) return `This list is over Wix’s limit of ${number(PER_SITE)} redirects per site. Use a group redirect where a whole folder moved, or keep the pages people still visit.`;
    if (result.mode === 'pairs' && source.sitemap) return NOTES.sitemap;
    const made = source.made || {};
    if (made.oldIndex || made.newIndex || made.index) return `Your ${made.newIndex && !made.oldIndex ? 'new' : 'old'} list is a sitemap index, which lists other sitemaps. Paste the text of the sitemaps it lists instead.`;
    if (source.waitingForNew) return 'Now paste your new Wix addresses, so each old one can be matched.';
    if (!counts.total && !(result.unmatched || []).length) {
      if (counts.grouped) return 'Every row is covered by your group redirect. Add it in Wix with the steps above.';
      const same = source.made && source.made.same ? source.made.same.length : 0;
      if (same && result.platform === 'wix') return 'Your new folder names are the same as the old ones. Type the new ones under “Change folder names”.';
      if (same) return `These addresses would stay the same on Wix, so they need no redirect.`;
      return EMPTY[result.mode] || EMPTY.pairs;
    }
    const issue = id => result.issues.find(item => item.id === id);
    const count = id => (issue(id) || {}).count || 0;
    const todo = [];
    if (count('chain')) todo.push(count('chain') === 1 ? 'flatten the chain' : `flatten ${number(count('chain'))} chains`);
    if (count('duplicate')) todo.push(count('duplicate') === 1 ? 'merge the duplicate' : `merge ${number(count('duplicate'))} duplicates`);
    if (count('conflict')) todo.push(`pick one new address for ${count('conflict') === 1 ? issue('conflict').rows[0].a.value : `${number(count('conflict'))} old addresses`}`);
    if (count('loop')) todo.push(`break ${counted(count('loop'), 'the loop', 'the loops')}`);
    const otherBlocked = result.rows.filter(row => row.status === 'blocked' && !row.issues.some(item => item.id === 'conflict' || item.id === 'loop')).length;
    if (otherBlocked) todo.push(`fix ${otherBlocked === 1 ? 'the blocked row' : `${number(otherBlocked)} blocked rows`}`);
    const waiting = (result.unmatched || []).length;
    const sentences = [];
    if (todo.length) sentences.push(`Next: ${joinWords(todo)}.`);
    if (counts.check) sentences.push(`${todo.length ? 'Then look' : 'Look'} over ${counts.check === 1 ? 'the row' : `the ${number(counts.check)} rows`} marked Check.`);
    if (waiting) sentences.push(`Give the old ${counted(waiting, 'address', 'addresses')} below a destination, or leave ${counted(waiting, 'it', 'them')} out.`);
    if (counts.suggested) sentences.push('Open a few suggested addresses on your new site to make sure each page exists.');
    if (!sentences.length) return 'Your file is ready. Download it, then import it in Wix.';
    if (!todo.length) sentences.push('Then download your file.');
    return sentences.join(' ');
  }

  const sideWords = sides => (sides.length === 2 ? 'Both addresses have' : sides[0] === 'old' ? 'The old address has' : 'The new address has');

  // Each kind of finding, once for the list (title and what to do) and once
  // per row. Levels: blocked = Wix would refuse it or it can't work, so the
  // row stays out of the file; fix = it will cause trouble; check = a
  // judgement call.
  const ISSUES = Object.freeze({
    unclear: { title: n => `${plural(n, 'line')} we couldn’t split`, detail: 'Put a comma or a tab between the old and the new address.' },
    'missing-old': { title: n => `${plural(n, 'line')} with no old address`, detail: 'Each line needs the old address first, then the new one.' },
    'missing-new': { title: n => `${plural(n, 'line')} with only one address`, detail: 'Add the new address after a comma or a tab. Until then, these stay out of the file.' },
    'old-invalid': { title: n => `${plural(n, 'old address', 'old addresses')} that ${counted(n, 'isn’t', 'aren’t')} a web address`, detail: 'Use a path like /about, or a full https:// address.' },
    'new-invalid': { title: n => `${plural(n, 'new address', 'new addresses')} that ${counted(n, 'isn’t', 'aren’t')} a web address`, detail: 'Use a path on your site like /about, or a full https:// address.' },
    'home-old': { title: () => 'Your home page as an old address', detail: 'The URL Redirect Manager can’t redirect your home page (/). Wix’s other option is a redirect from one domain to another.' },
    fragment: { title: n => `${plural(n, 'old address', 'old addresses')} with a #`, detail: 'Wix doesn’t accept # in an old address. The part after # never reaches your site, so it can go.', bulk: { action: 'fragment-all', label: n => (n === 1 ? 'Remove the # part' : `Remove the # part from all ${number(n)}`) } },
    self: { title: n => `${plural(n, 'redirect')} to the same address`, detail: 'A redirect to its own address is a loop, so the page never loads.', bulk: { action: 'self-all', label: n => (n === 1 ? 'Remove it' : `Remove all ${number(n)}`) } },
    conflict: { title: n => `${plural(n, 'old address', 'old addresses')} with two new addresses`, detail: 'An old address can only go to one page. Keep one in the table. Until then, neither goes in the file.' },
    loop: { title: n => `${plural(n, 'redirect')} in a loop`, detail: 'They lead back to where they started, so the page never loads. Wix blocks loops. Change or remove one redirect in each.' },
    lang: { title: n => `${plural(n, 'new address', 'new addresses')} with ?lang=`, detail: 'Wix says a new address can’t contain ?lang=. On a multilingual site, redirects stay within one language.' },
    chain: { title: n => plural(n, 'chain'), detail: 'These point at an address that redirects again. Wix’s developer docs say Wix then deletes the other redirect, and they don’t cover imports. Point each one straight at the final page.', bulk: { action: 'flatten-all', label: n => (n === 1 ? 'Flatten the chain' : `Flatten all ${number(n)} chains`) } },
    duplicate: { title: n => plural(n, 'duplicate row'), detail: 'The same redirect is listed more than once. The file keeps one.', bulk: { action: 'merge-all', label: n => (n === 1 ? 'Merge the duplicate' : `Merge all ${number(n)}`) } },
    'home-new': { title: n => `${plural(n, 'page')} sent to the home page`, detail: 'Google warns that sending many old pages to the home page can be treated as a soft 404. Pick the closest category or page. If nothing fits, leave the row out: “not found” is right for a page that’s gone.' },
    'close-match': { title: n => `${plural(n, 'match', 'matches')} to check`, detail: 'These were matched by name, but not for certain. Use each one, or pick another.' },
    external: { title: n => `${plural(n, 'redirect')} to another website`, detail: 'Wix allows this. If it’s your own Wix site, add its address under “Your site address” to keep only the path.' },
    'other-domain': { title: n => `${plural(n, 'old address', 'old addresses')} on another domain`, detail: 'Wix uses only the path, on the domains connected to your site. To keep visitors from an old domain, Wix’s way is to connect it to your site as a second domain.' },
    'free-wix-old': { title: n => `${plural(n, 'old address', 'old addresses')} on a free Wix address`, detail: 'Wix can’t redirect from a free Wix address. The path is used on your own domain instead.' },
    'blog-target': { title: n => `${plural(n, 'redirect')} to a blog post`, detail: 'Wix’s help pages disagree about redirecting to a blog post. Test one first.' },
    'query-old': { title: n => `${plural(n, 'old address', 'old addresses')} with a ?`, detail: 'Wix doesn’t say whether a redirect matches the part after ?. Test one after importing.' },
    'query-new': { title: n => `${plural(n, 'new address', 'new addresses')} with a ?`, detail: 'Wix doesn’t say whether it keeps the part after ?. Test one after importing.' },
    'fragment-new': { title: n => `${plural(n, 'new address', 'new addresses')} with a #`, detail: 'Wix only says an old address can’t have one. Test one, or remove the # part.', bulk: { action: 'fragment-new-all', label: n => (n === 1 ? 'Remove the # part' : `Remove the # part from all ${number(n)}`) } },
    'case-only': { title: n => `${plural(n, 'row')} where only capitals differ`, detail: 'If Wix ignores capitals, these point to themselves. Test one after importing.' },
    caps: { title: n => `${plural(n, 'row')} with capital letters`, detail: 'Google treats /Candles and /candles as different addresses, and Wix doesn’t say whether its redirects do. Keep each address exactly as it’s used.' },
    symbols: { title: n => `${plural(n, 'row')} with spaces or symbols`, detail: 'Wix doesn’t say how its import reads these. Test one after importing.' },
    'non-ascii': { title: n => `${plural(n, 'row')} with letters outside A–Z`, detail: 'Google’s guide says to percent-encode them (é becomes %C3%A9). Wix doesn’t say which form its import wants: test one first.' },
    'too-long': { title: n => `${plural(n, 'row')} longer than Wix’s developer docs allow`, detail: 'They allow 950 characters for an old address and 4,096 for a new one, counted with letters like é written as %C3%A9.' },
    'group-overlap': { title: n => `${plural(n, 'row')} inside your group redirect’s folder`, detail: 'Wix says a group can’t use a path another redirect already uses. Test these after adding the group.' }
  });

  // The note under one row, with its one-tap fixes. Returns
  // { level, html, actions: [{ action, label }] }.
  const lineRef = line => (typeof line === 'number' ? `Line ${line}` : 'An added row');
  function rowNote(row, issue) {
    switch (issue.id) {
      case 'unclear': return { html: `Couldn’t tell where the old address ends. Put a comma or a tab between the two.` };
      case 'missing-old': return { html: 'No old address yet. Tap “Add the old address” to type it.' };
      case 'missing-new': return { html: 'No new address yet. Tap “Add the new address” to type it.' };
      case 'old-invalid': return { html: `${code(shorten(row.a.typed, 40))} isn’t a web address.` };
      case 'new-invalid': return { html: `${code(shorten(row.b.typed, 40))} isn’t a web address.` };
      case 'home-old': return { html: 'Wix can’t redirect your home page with the URL Redirect Manager.' };
      case 'fragment': return { html: `Wix doesn’t accept # in an old address.`, actions: [{ action: 'fragment', label: `Remove ${shorten(`#${row.a.fragment}`, 28)}` }] };
      case 'self': return { html: issue.slashOnly ? 'Only the / at the end is different, and Wix’s developer docs treat both as one path, so this points to itself.' : 'It points to itself.', actions: [{ action: 'remove', label: 'Remove it' }] };
      case 'conflict': return { html: issue.others.map(other => `${lineRef(other.line)} sends it to ${code(shorten(other.to))}.`).join(' '), actions: [{ action: 'keep', label: 'Keep this one' }] };
      case 'loop': return { html: `${issue.into ? 'Leads into a loop' : 'Loop'}: ${issue.cycle.map(step => (step === '…' ? '…' : code(shorten(step, 40)))).join(' → ')}. Change or remove one of these.` };
      case 'lang': return { html: 'Wix says the new address can’t contain ?lang=.' };
      case 'chain': {
        const steps = issue.steps.length > 5 ? [issue.steps[0], issue.steps[1], '…', issue.steps[issue.steps.length - 1]] : issue.steps;
        return { html: `Chain: ${steps.map(step => (step === '…' ? '…' : code(shorten(step, 44)))).join(' → ')}.`, actions: [{ action: 'flatten', label: `Go straight to ${shorten(issue.final, 40)}` }] };
      }
      case 'duplicate': return { html: typeof issue.of === 'number' ? `Same as line ${issue.of}.` : 'Same as an added row.', actions: [{ action: 'remove', label: 'Merge' }] };
      case 'home-new': return { html: 'Goes to the home page. Is there a closer category or page?' };
      case 'close-match': {
        const others = row.alternatives.length ? ` ${counted(row.alternatives.length, 'Another address', `${number(row.alternatives.length)} other addresses`)} could fit too.` : '';
        const lead = issue.kind === 'ambiguous' ? 'More than one new address fits this name.' : issue.kind === 'folder' ? 'Same name, but the old folder doesn’t show what kind of page this was.' : 'Matched by a similar name, not the same one.';
        return { html: `${lead}${others}`, actions: [{ action: 'accept', label: 'Use this' }, { action: 'pick', label: 'Pick another' }] };
      }
      case 'external': return { html: `Goes to another website: ${code(row.b.host)}.` };
      case 'other-domain': return { html: `On ${code(row.a.host)}, not your site’s domain. Only the path is used.` };
      case 'free-wix-old': return { html: 'A free Wix address. Only the path is used, on your own domain.' };
      case 'blog-target': return { html: 'Goes to a blog post. Wix’s help pages disagree about this: test one first.' };
      case 'query-old': return { html: `Has ${code(shorten(`?${row.a.query}`, 32))}. Test it after importing.` };
      case 'query-new': return { html: `Has ${code(shorten(`?${row.b.query}`, 32))}. Test it after importing.` };
      case 'fragment-new': return { html: `Ends in ${code(shorten(`#${row.b.fragment}`, 32))}. Test it, or remove it.`, actions: [{ action: 'fragment-new', label: 'Remove it' }] };
      case 'case-only': return { html: 'Only the capitals differ. If Wix ignores capitals, this points to itself.' };
      case 'caps': return { html: `${sideWords(issue.sides)} capital letters. Keep them only if the page’s address really has them.` };
      case 'symbols': return { html: `${sideWords(issue.sides)} a space or a symbol. Test it after importing.` };
      case 'non-ascii': return { html: `${sideWords(issue.sides)} letters outside A–Z. Test it after importing.` };
      case 'too-long': return { html: `Longer, once encoded, than the ${issue.sides.includes('old') ? '950' : '4,096'} characters Wix’s developer docs allow.` };
      case 'group-overlap': return { html: `Your group redirect also covers ${code(`/${issue.oldPrefix}/`)}. Test which one Wix uses.` };
      default: return { html: '' };
    }
  }

  function rowNotes(row) {
    const notes = row.issues.map(issue => ({ level: issue.level, ...rowNote(row, issue) }));
    if (row.fixed && row.fixed.kind === 'chain') notes.push({ level: 'fixed', html: `Fixed: goes straight to ${code(shorten(row.b.value, 44))}. It went through ${code(shorten(row.fixed.before, 44))} first.`, actions: [{ action: 'undo', label: 'Undo' }] });
    else if (row.fixed && row.fixed.kind === 'fragment') notes.push({ level: 'fixed', html: `Fixed: removed ${code(shorten(row.fixed.removed || '#', 32))}.`, actions: [{ action: 'undo', label: 'Undo' }] });
    if (row.origin === 'pattern' && !row.issues.length) notes.push({ level: 'suggested', html: `Suggested (${esc(row.platformLabel)} ${esc(row.ruleLabel.toLowerCase())}): open it on your new site to make sure this page exists.` });
    return notes;
  }

  // ---- Markup shared by the server render and the browser --------------------------

  const ICONS = Object.freeze({
    copy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><rect x="8" y="8" width="12" height="13" rx="2"/><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3"/></svg>',
    download: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v11"/><path d="m7 10.5 5 5 5-5"/><path d="M5 19.5h14"/></svg>',
    remove: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="m7 7 10 10M17 7 7 17"/></svg>'
  });
  const chip = status => `<span class="wrb-chip" data-status="${status}"><span class="wrb-chip-icon" aria-hidden="true"></span>${STATUS_WORDS[status]}</span>`;

  // One line per part on the page; the hidden " · " keeps the element's text
  // the same as summaryText().
  function summaryMarkup(result) {
    const items = summaryItems(result);
    if (!items.length) return '<span class="wrb-summary-empty">No redirects yet</span>';
    return items.map((item, index) => `${index ? '<span class="wrb-dot" aria-hidden="true"> · </span>' : ''}<span class="wrb-summary-part" data-kind="${item.kind}">${esc(item.text)}</span>`).join('');
  }

  const actionButton = (action, label, extra = '') => `<button type="button" class="wrb-fix" data-wrb-action="${action}"${extra}>${esc(label)}</button>`;

  function issuesMarkup(result) {
    return result.issues.map(issue => {
      const info = ISSUES[issue.id];
      if (!info) return '';
      const bulk = info.bulk && issue.count ? actionButton(info.bulk.action, info.bulk.label(issue.count)) : '';
      const show = `<button type="button" class="wrb-text-button" data-wrb-action="show" data-wrb-issue="${issue.id}">Show ${counted(issue.rows.length, 'the row', 'these rows')}</button>`;
      return `<li class="wrb-issue" data-level="${issue.level}" data-issue="${issue.id}"><span class="wrb-issue-icon" aria-hidden="true"></span><div><p class="wrb-issue-title"><span class="wrb-visually-hidden">${STATUS_WORDS[issue.level]}: </span>${esc(info.title(issue.count))}</p><p class="wrb-issue-detail">${esc(info.detail)}</p><div class="wrb-issue-actions">${bulk}${show}</div></div></li>`;
    }).join('');
  }

  // Short notes about what the tool did to the list without being asked.
  function notesMarkup(result) {
    const notes = [];
    const source = result.source || {};
    const tidy = result.tidy;
    if (source.header) {
      const heading = esc(`“${shorten(source.header.cells.join(', '), 40)}”`);
      notes.push(`Skipped line ${source.header.line}, <span class="wrb-nowrap">${heading}</span>, as a heading. ${actionButton('keep-header', 'It’s a redirect', ' data-wrb-inline')}`);
    }
    const done = [];
    if (tidy['host-old']) done.push(`kept only the path of ${plural(tidy['host-old'], 'old address', 'old addresses')} (Wix’s Old URL is a path)`);
    if (tidy['host-new'] && result.mode !== 'match') done.push(`turned ${plural(tidy['host-new'], 'new address', 'new addresses')} on your site into paths`);
    if (tidy['trailing-slash']) done.push(`removed the / at the end of ${plural(tidy['trailing-slash'], 'address', 'addresses')} (Wix’s developer docs treat /a/ and /a as one path)`);
    if (tidy['double-slash']) done.push(`removed doubled slashes from ${plural(tidy['double-slash'], 'address', 'addresses')}`);
    if (tidy['leading-slash']) done.push(`added a / to the start of ${plural(tidy['leading-slash'], 'address', 'addresses')}`);
    if (tidy['empty-query']) done.push(`removed an empty ? from ${plural(tidy['empty-query'], 'address', 'addresses')}`);
    if (done.length) notes.push(`We tidied the list: ${esc(joinWords(done))}.`);
    if (result.context.inferred && result.context.hostsConverted && result.mode === 'pairs') notes.push(`Treated ${code(result.context.inferred)} as your site, because your old addresses use it.`);
    if (result.siteSuggestion) notes.push(`Is ${code(result.siteSuggestion)} your Wix site? ${actionButton('use-site', 'Yes, use it', ` data-wrb-value="${esc(result.siteSuggestion)}" data-wrb-inline`)}`);
    if (result.domainMove) notes.push(`Your old addresses are on ${code(result.domainMove)}. Wix uses only their paths, on the domains connected to your site. For a move between domains, Wix’s way is to connect the old domain to your site as a second domain.`);
    if (source.extra) notes.push(`Used the first two columns on ${plural(source.extra, 'line')} and left the rest out.`);
    if (source.truncated) notes.push(`Read the first ${number(MAX_LINES)} lines only.`);
    if (source.comments) notes.push(`Skipped ${plural(source.comments, 'comment line')}.`);
    const made = source.made;
    if (made && result.mode === 'match') {
      if (made.oldCount && made.newCount) {
        const { exact, close } = made.counts;
        const sure = exact ? `${plural(exact, 'old address', 'old addresses')} ${counted(exact, 'has', 'have')} a page with the same name.` : '';
        const check = close ? ` ${plural(close, exact ? 'more match' : 'match', exact ? 'more matches' : 'matches')} ${counted(close, 'needs', 'need')} a check.` : '';
        if (sure || check) notes.push(`${sure}${check}`.trim());
      }
      if (made.freeWix) notes.push(`Your new addresses are on a free Wix address, so we kept the part after the site name. Redirects only work once your own domain is connected.`);
    }
    if (made && result.mode === 'pattern' && made.same.length) {
      const examples = made.same.slice(0, 3).map(item => code(item.from));
      const listed = made.same.length > 3 ? `${examples.join(', ')} and more` : joinWords(examples);
      notes.push(`${plural(made.same.length, 'address', 'addresses')} would stay the same on Wix: ${listed}. ${counted(made.same.length, 'It needs', 'They need')} no redirect if the new ${counted(made.same.length, 'page has', 'pages have')} the same name.`);
    }
    if (made) {
      if (made.skipped.home) notes.push('Left out your home page: the URL Redirect Manager can’t redirect it.');
      if (made.skipped.homeQuery) {
        const n = made.skipped.homeQuery;
        notes.push(`Left out ${plural(n, 'address', 'addresses')} like ${code(shorten(made.skipped.homeQueryExample, 32))}: ${counted(n, 'it starts', 'they start')} at your home page, which the URL Redirect Manager can’t redirect.`);
      }
      if (made.skipped.repeated) notes.push(`Listed ${plural(made.skipped.repeated, 'repeated old address', 'repeated old addresses')} once.`);
      if (made.skipped.notAddress) notes.push(`Skipped ${plural(made.skipped.notAddress, 'line')} that ${counted(made.skipped.notAddress, 'isn’t an address', 'aren’t addresses')}.`);
    }
    return notes.map(note => `<li>${note}</li>`).join('');
  }

  function groupsMarkup(result) {
    const steps = '<ol class="wrb-group-steps"><li>In Wix, go to <b>SEO &amp; GEO</b> › <b>Tools and settings</b> › <b>URL Redirect Manager</b>.</li><li>Click <b>+ New Redirect</b> and choose <b>Group redirect</b>. Wix’s group redirect page calls the button + Create New Redirect.</li><li>Fill in <b>Old URL Path</b> and <b>New URL Path</b> as shown, then click <b>Save &amp; Close</b>.</li></ol>';
    // Wix's developer docs: in a group, "every path under `from` matches. The
    // rest of the URL carries over to the target".
    const limits = '<p class="wrb-group-limits">Wix’s limits for a group: no spaces, and no path that another redirect already uses. It can’t redirect from your home page, use another domain or send a whole group to one page. A group covers every address in the old folder, even ones you didn’t list. An old product you didn’t move goes to a new address that doesn’t exist, so test one.</p>';
    const fields = group => `<dl class="wrb-group-fields"><div><dt>Old URL Path</dt><dd>${code(group.oldPrefix)}</dd></div><div><dt>New URL Path</dt><dd>${code(group.newPrefix)}</dd></div></dl>`;
    const offered = result.groups.map(group => `<section class="wrb-group" data-group="${esc(group.key)}" aria-label="${esc(`Group redirect from ${group.oldPrefix} to ${group.newPrefix}`)}">
          <p class="wrb-group-kicker">One group redirect can do this</p>
          <h3>${esc(`${plural(group.count, 'row')} only change ${counted(group.count, 'its', 'their')} folder`)}</h3>
          <p>Each keeps its name, like ${code(group.examples[0].from)} → ${code(group.examples[0].to)}. One group redirect in Wix can replace these rows.${group.exceptions ? ` ${plural(group.exceptions, 'other row')} in ${code(`/${group.oldPrefix}/`)} ${counted(group.exceptions, 'doesn’t', 'don’t')} follow it, so ${counted(group.exceptions, 'it stays', 'they stay')} in the file.` : ''}</p>
          ${fields(group)}
          <div class="wrb-group-actions">${actionButton('group-use', `Use a group redirect for these ${number(group.count)}`, ` data-wrb-value="${esc(group.key)}"`)}<span class="wrb-group-default">Until you choose, they stay in the file. Keep them there if you renamed any of these pages.</span></div>
          <details class="wrb-group-how"><summary>How to add a group redirect in Wix<span class="wrb-disclosure-icon" aria-hidden="true"></span></summary><div>${steps}${limits}</div></details>
        </section>`);
    const accepted = (result.acceptedGroups || []).map(group => `<section class="wrb-group" data-group="${esc(group.key)}" data-accepted="true" aria-label="${esc(`Group redirect from ${group.oldPrefix} to ${group.newPrefix}`)}">
          <p class="wrb-group-kicker">Group redirect: add it in Wix by hand</p>
          <h3>${esc(`${plural(group.count, 'row')} left the file for one group redirect`)}</h3>
          ${fields(group)}
          ${steps}${limits}
          <div class="wrb-group-actions">${actionButton('group-undo', 'Put them back in the file', ` data-wrb-value="${esc(group.key)}" data-wrb-quiet`)}</div>
        </section>`);
    return accepted.concat(offered).join('');
  }

  const FILTERS = Object.freeze([['all', 'All'], ['attention', 'Needs attention'], ['ready', 'Ready']]);
  const inFilter = (row, filter, issue) => {
    if (filter === 'attention') return ['check', 'fix', 'blocked'].includes(row.status);
    if (filter === 'ready') return ['ready', 'fixed', 'suggested'].includes(row.status);
    if (filter === 'issue') return row.issues.some(item => item.id === issue);
    return true;
  };

  function filtersMarkup(result, view = {}) {
    const filter = view.filter || 'all';
    const count = id => result.rows.filter(row => inFilter(row, id)).length;
    const chips = FILTERS.map(([id, label]) => `<button type="button" class="wrb-filter" data-wrb-filter="${id}" aria-pressed="${filter === id}">${label} <span class="wrb-filter-count">${number(count(id))}</span></button>`);
    if (filter === 'issue' && ISSUES[view.issue]) {
      const shown = result.rows.filter(row => inFilter(row, 'issue', view.issue)).length;
      chips.push(`<button type="button" class="wrb-filter" data-wrb-filter="issue" aria-pressed="true">${esc(ISSUES[view.issue].title(shown).replace(/^[\d,]+ /, ''))} <span class="wrb-filter-count">${number(shown)}</span></button>`);
    }
    return chips.join('');
  }

  function cellMarkup(row, side, editable) {
    const address = side === 'from' ? row.a : row.b;
    const shown = address.kind === 'empty' ? '' : address.kind === 'invalid' ? address.typed : address.value;
    const which = side === 'from' ? 'old' : 'new';
    const content = shown ? `<span class="wrb-address" dir="ltr">${breakable(shown)}</span>` : `<span class="wrb-missing">Add the ${which} address</span>`;
    if (!editable) return content;
    const where = row.added ? 'in the added redirect' : `on line ${esc(row.line)}`;
    return `<button type="button" class="wrb-cell" data-wrb-edit="${side}" data-key="${esc(row.key)}"><span class="wrb-visually-hidden">Edit the ${which} address ${where}: </span>${content}</button>`;
  }

  function rowMarkup(row, mode) {
    const notes = rowNotes(row);
    const editOld = mode === 'pairs' || row.added;
    const items = notes.map(note => `<li data-level="${note.level}">${note.html}</li>`).join('');
    const actions = notes.flatMap(note => note.actions || []).map(item => actionButton(item.action, item.label, ` data-key="${esc(row.key)}"`)).join('');
    const line = row.added ? 'Added' : `<span class="wrb-line-word">Line </span>${esc(row.line)}`;
    return `<tr class="wrb-row" role="row" data-status="${row.status}" data-key="${esc(row.key)}">
            <td class="wrb-line" role="cell">${line}</td>
            <td class="wrb-old" role="cell" data-label="Old address">${cellMarkup(row, 'from', editOld)}</td>
            <td class="wrb-new" role="cell" data-label="New address">${cellMarkup(row, 'to', true)}</td>
            <td class="wrb-state" role="cell">${chip(row.status)}</td>
            <td class="wrb-what" role="cell">${items ? `<ul class="wrb-row-notes">${items}</ul>` : ''}${actions ? `<div class="wrb-row-actions">${actions}</div>` : ''}</td>
            <td class="wrb-tools" role="cell"><button type="button" class="wrb-remove" data-wrb-action="remove" data-key="${esc(row.key)}" aria-label="${row.added ? 'Remove the added redirect' : `Remove line ${esc(row.line)}`}">${ICONS.remove}</button></td>
          </tr>`;
  }

  // The rows the table shows for a filter, up to the current page size.
  function visibleRows(result, view = {}) {
    const all = result.rows.filter(row => inFilter(row, view.filter || 'all', view.issue));
    return { rows: all.slice(0, view.limit || PAGE_SIZE), total: all.length };
  }

  function tableRowsMarkup(result, view = {}) {
    const { rows, total } = visibleRows(result, view);
    if (!total) {
      const empty = !result.rows.length ? (result.unmatched.length ? 'Every old address still needs a destination: see the list below.' : EMPTY[result.mode] || EMPTY.pairs) : view.filter === 'attention' ? 'Nothing needs your attention.' : 'No rows here.';
      return `<tr class="wrb-empty-row" role="row"><td colspan="6" role="cell">${esc(empty)}</td></tr>`;
    }
    return rows.map(row => rowMarkup(row, result.mode)).join('');
  }

  function moreMarkup(result, view = {}) {
    const { rows, total } = visibleRows(result, view);
    if (total <= rows.length) return '';
    return `<button type="button" class="wrb-more" data-wrb-action="more">Show ${number(Math.min(PAGE_SIZE, total - rows.length))} more <span>(${number(rows.length)} of ${number(total)} shown)</span></button>`;
  }

  function unmatchedMarkup(result, view = {}) {
    const list = result.unmatched || [];
    if (!list.length) return '';
    const shown = list.slice(0, view.waitingLimit || 50);
    const why = result.mode === 'match' ? 'No new address has a name like these.' : 'No pattern fits these.';
    const datalist = result.mode === 'match' ? ' list="wrb-new-options"' : '';
    const items = shown.map((row, index) => `<li><code dir="ltr">${esc(row.from)}</code><div class="wrb-dest"><label class="wrb-visually-hidden" for="wrb-dest-${index}">New address for ${esc(row.from)}</label><input id="wrb-dest-${index}" type="text" inputmode="url" autocomplete="off" autocapitalize="off" spellcheck="false" dir="ltr" data-wrb-dest data-key="${esc(row.key)}" placeholder="New address"${datalist}><button type="button" class="wrb-fix" data-wrb-action="dest" data-key="${esc(row.key)}">Add</button></div></li>`).join('');
    const more = list.length > shown.length ? `<button type="button" class="wrb-more" data-wrb-action="more-waiting">Show ${number(Math.min(50, list.length - shown.length))} more</button>` : '';
    return `<h3 id="wrb-waiting-title">${esc(`${plural(list.length, 'old address', 'old addresses')} ${counted(list.length, 'needs', 'need')} a destination`)}</h3>
          <p>${why} Type a new address, or leave ${counted(list.length, 'it', 'them')} out. An old page with no redirect shows “not found” (404), which Google says is right for a page that’s gone.</p>
          <ul class="wrb-waiting-list">${items}</ul>${more}`;
  }

  function datalistMarkup(result) {
    const made = result.source && result.source.made;
    if (result.mode !== 'match' || !made) return '';
    return made.options.slice(0, MAX_LINES).map(value => `<option value="${esc(value)}"></option>`).join('');
  }

  function fileChoiceMarkup(result, index = 0) {
    if (result.files.length < 2) return '';
    return result.files.map(file => `<button type="button" class="wrb-file-choice" data-wrb-action="file" data-wrb-value="${file.index}" aria-pressed="${file.index === index}">File ${file.index + 1} <span>rows ${number(file.first)}–${number(file.last)}</span></button>`).join('');
  }

  const fileNote = result => fileNoteParts(result).join(' ');
  // On the page the first part (what's in the file) is bold, the rest not.
  function fileNoteMarkup(result) {
    const [first, ...rest] = fileNoteParts(result);
    return `<strong>${esc(first)}</strong>${rest.length ? ` ${esc(rest.join(' '))}` : ''}`;
  }
  function fileNoteParts(result) {
    const { counts, files } = result;
    if (!counts.exported) return [counts.blocked ? 'Nothing can go in the file yet: every row is blocked.' : 'Nothing in the file yet.'];
    const parts = [files.length > 1
      ? `${plural(counts.exported, 'redirect')} in ${number(files.length)} files of up to ${PER_FILE} (Wix’s limit per file). Import them one at a time. Wix allows ${number(PER_SITE)} per site in all.`
      : `${plural(counts.exported, 'redirect')} in the file.`];
    if (counts.blocked) parts.push(`${plural(counts.blocked, 'row')} ${counted(counts.blocked, 'stays', 'stay')} out until you fix ${counted(counts.blocked, 'it', 'them')}.`);
    if (counts.duplicatesLeft) parts.push('Repeated rows are written once.');
    // Wix's developer docs: a redirect that points at another redirect's old
    // address replaces it, or fails in a bulk request; imports aren't covered.
    if (counts.chainsLeft) parts.push(`${plural(counts.chainsLeft, 'redirect')} in the file still ${counted(counts.chainsLeft, 'points', 'point')} at an address that redirects again: flatten ${counted(counts.chainsLeft, 'it', 'them')} first.`);
    return parts;
  }

  const currentFile = (result, index) => result.files[Math.min(Math.max(0, index || 0), Math.max(0, result.files.length - 1))] || null;
  const csvText = (result, index) => {
    const file = currentFile(result, index);
    return file ? file.csv : buildCsv([]);
  };
  const downloadLabel = (result, index) => {
    const file = currentFile(result, index);
    return file && result.files.length > 1 ? `Download file ${file.index + 1}` : 'Download CSV';
  };

  // The rules a pattern uses, drawn from the same data the matcher reads.
  function ruleExample(rule, settings, prefixes) {
    const from = rule.from.map(token => {
      if (token === '{slug}') return '<span class="wrb-var">name</span>';
      if (token === '**') return '…';
      if (token.startsWith('{opt:')) return esc(splitFolders(settings[token.slice(5, -1)]).join('/') || '…');
      if (token.startsWith('*')) return `<span class="wrb-var">${esc(token.slice(1))}</span>`;
      if (token === '#yyyy') return '2025';
      if (token === '#mm') return '04';
      if (token === '#dd') return '30';
      return esc(token);
    }).join('/<wbr>');
    const folder = splitFolders(prefixes[rule.to] || '');
    const to = folder.length ? `${esc(folder.join('/'))}/<wbr><span class="wrb-var">name</span>` : '<span class="wrb-var">name</span>';
    return { from: `/${from}`, to: `/${to}` };
  }

  function rulesMarkup(platform, settingsInput, prefixesInput) {
    const id = PLATFORMS[platform] ? platform : 'shopify';
    const settings = settingsFor(id, settingsInput || {});
    const prefixes = { ...DEFAULT_PREFIXES, ...(prefixesInput || {}) };
    const rows = activeRules(id, settings).map(rule => {
      const example = ruleExample(rule, settings, prefixes);
      const same = rule.to === '' && rule.from.length === 1;
      return `<tr><th scope="row">${esc(rule.label)}</th><td><code>${example.from}</code></td><td>${same ? '<span class="wrb-same">Same address</span>' : `<code>${example.to}</code>`}</td></tr>`;
    }).join('');
    return `<table class="wrb-rules-table"><caption>How addresses change</caption><thead><tr><th scope="col">Page</th><th scope="col">Old address</th><th scope="col">New on Wix</th></tr></thead><tbody>${rows}</tbody></table>`;
  }

  function hintText(result, isSample) {
    if (!result.counts.exported) return '';
    if (isSample) return 'This is the made-up example. Paste your own list above to make your file.';
    return '';
  }

  // Everything the result area shows, as markup or text, for one state.
  function views(result, view = {}) {
    return {
      summary: summaryMarkup(result),
      next: keepPaths(esc(result.next)),
      issues: issuesMarkup(result),
      notes: notesMarkup(result),
      groups: groupsMarkup(result),
      filters: filtersMarkup(result, view),
      rows: tableRowsMarkup(result, view),
      more: moreMarkup(result, view),
      waiting: unmatchedMarkup(result, view),
      fileChoice: fileChoiceMarkup(result, view.file || 0),
      fileNote: fileNoteMarkup(result),
      fileName: esc((currentFile(result, view.file) || { name: 'wix-redirects.csv' }).name),
      csv: csvText(result, view.file || 0),
      download: esc(downloadLabel(result, view.file || 0))
    };
  }

  function announcement(result) {
    const parts = summaryParts(result);
    return parts.length ? `${parts.join(', ')}.` : 'No redirects yet.';
  }

  // ---- Page behaviour ---------------------------------------------------------------

  function init(root) {
    if (!root || root.dataset.wrbReady === 'true') return;
    root.dataset.wrbReady = 'true';
    const find = selector => root.querySelector(selector);
    const findAll = selector => Array.from(root.querySelectorAll(selector));
    const fields = {
      pairs: find('[data-wrb-input="pairs"]'),
      old: find('[data-wrb-input="old"]'),
      new: find('[data-wrb-input="new"]'),
      pattern: find('[data-wrb-input="pattern"]')
    };
    const site = find('[data-wrb-site]');
    const platformSelect = find('[data-wrb-platform]');
    const announcer = find('[data-wrb-announcement]');
    const csvBox = find('[data-wrb-csv]');
    const fallback = find('[data-wrb-fallback]');
    const fallbackText = find('[data-wrb-fallback-text]');
    if (!fields.pairs || !csvBox) return;

    const perMode = () => ({ overrides: {}, added: [], groups: [], keepHeader: false });
    const memory = { pairs: perMode(), match: perMode(), pattern: perMode() };
    const view = { filter: 'all', issue: '', limit: PAGE_SIZE, file: 0, waitingLimit: 50 };
    const memo = {};
    let result = null;
    let editing = null;
    let addedCount = 0;
    let computeTimer = 0;
    let announceTimer = 0;
    let reportedBuilt = false;
    const urls = new Map();

    const toolEvent = name => document.dispatchEvent(new CustomEvent('myuser:tool-event', { detail: { name } }));
    const modeValue = () => (findAll('input[name="wrb-mode"]').find(input => input.checked) || {}).value || 'pairs';
    const settingsValue = () => {
      const settings = {};
      PLATFORM_IDS.forEach(id => { settings[id] = {}; });
      findAll('[data-wrb-setting]').forEach(input => {
        const [id, key] = input.dataset.wrbSetting.split('.');
        if (settings[id]) settings[id][key] = input.type === 'checkbox' ? input.checked : input.value;
      });
      return settings;
    };
    const prefixesValue = () => {
      const prefixes = {};
      findAll('[data-wrb-prefix]').forEach(input => { prefixes[input.dataset.wrbPrefix] = input.value; });
      return prefixes;
    };
    function readState() {
      const mode = modeValue();
      const memoryForMode = memory[mode];
      return {
        mode,
        site: site ? site.value : '',
        pairs: fields.pairs.value,
        old: fields.old ? fields.old.value : '',
        new: fields.new ? fields.new.value : '',
        pattern: fields.pattern ? fields.pattern.value : '',
        platform: platformSelect ? platformSelect.value : 'shopify',
        settings: settingsValue(),
        prefixes: prefixesValue(),
        overrides: memoryForMode.overrides,
        added: memoryForMode.added,
        groups: memoryForMode.groups,
        keepHeader: memoryForMode.keepHeader
      };
    }
    const isSample = state => (state.mode === 'pairs' ? state.pairs === SAMPLE : state.mode === 'match' ? state.old === SAMPLE_OLD && state.new === SAMPLE_NEW : state.pattern === SAMPLE_PATTERN);

    // Rewriting unchanged markup would replace a button between its mousedown
    // and its click, and the click would never arrive.
    const shown = new Map();
    function setHtml(element, html) {
      if (!element || shown.get(element) === html) return;
      shown.set(element, html);
      element.innerHTML = html;
    }

    const lineCount = value => (value.trim() ? value.trim().split(/\r\n|\n|\r/).length : 0);
    function updateCounts() {
      Object.entries(fields).forEach(([name, input]) => {
        const label = find(`[data-wrb-count="${name}"]`);
        if (input && label) label.textContent = `${plural(lineCount(input.value), 'line')}`;
        const clear = find(`[data-wrb-clear="${name === 'old' || name === 'new' ? 'match' : name}"]`);
        if (clear) clear.disabled = name === 'old' || name === 'new' ? !fields.old.value && !fields.new.value : !input.value;
      });
      findAll('[data-wrb-sample-note]').forEach(note => {
        const state = readState();
        note.hidden = !(note.dataset.wrbSampleNote === state.mode && isSample(state));
      });
    }

    function showPanels(state) {
      root.dataset.wrbMode = state.mode;
      findAll('[data-wrb-panel]').forEach(panel => { panel.hidden = panel.dataset.wrbPanel !== state.mode; });
      findAll('[data-wrb-platform-settings]').forEach(panel => { panel.hidden = panel.dataset.wrbPlatformSettings !== state.platform; });
      const rules = find('[data-wrb-rules]');
      if (rules) setHtml(rules, rulesMarkup(state.platform, state.settings[state.platform], state.prefixes));
    }

    function render(announce) {
      const state = readState();
      showPanels(state);
      result = compute(state, memo);
      if (view.file >= result.files.length) view.file = Math.max(0, result.files.length - 1);
      if (view.filter === 'issue' && !result.rows.some(row => row.issues.some(issue => issue.id === view.issue))) {
        view.filter = 'all';
        view.issue = '';
      }
      const parts = views(result, view);
      setHtml(find('[data-wrb-summary]'), parts.summary);
      setHtml(find('[data-wrb-next]'), parts.next);
      const issues = find('[data-wrb-issues]');
      setHtml(issues, parts.issues);
      issues.hidden = !parts.issues;
      const notes = find('[data-wrb-notes]');
      setHtml(notes, parts.notes);
      notes.hidden = !parts.notes;
      const groups = find('[data-wrb-groups]');
      setHtml(groups, parts.groups);
      groups.hidden = !parts.groups;
      setHtml(find('[data-wrb-filters]'), parts.filters);
      setHtml(find('[data-wrb-rows]'), parts.rows);
      const more = find('[data-wrb-more]');
      setHtml(more, parts.more);
      more.hidden = !parts.more;
      const waiting = find('[data-wrb-waiting]');
      setHtml(waiting, parts.waiting);
      waiting.hidden = !parts.waiting;
      setHtml(find('[data-wrb-options]'), datalistMarkup(result));
      const boxNotes = inputNotes(result);
      findAll('[data-wrb-input-note]').forEach(note => {
        const message = boxNotes[note.dataset.wrbInputNote] || '';
        if (note.textContent !== message) note.textContent = message;
        note.hidden = !message;
      });
      const choice = find('[data-wrb-file-choice]');
      setHtml(choice, parts.fileChoice);
      choice.hidden = !parts.fileChoice;
      setHtml(find('[data-wrb-file-note]'), parts.fileNote);
      find('[data-wrb-file-name]').textContent = (currentFile(result, view.file) || { name: 'wix-redirects.csv' }).name;
      if (csvBox.value !== parts.csv) csvBox.value = parts.csv;
      const download = find('[data-wrb-download]');
      setHtml(find('[data-wrb-download-label]'), parts.download);
      download.disabled = !result.counts.exported;
      find('[data-wrb-copy]').disabled = !result.counts.exported;
      find('[data-wrb-hint]').textContent = hintText(result, isSample(state));
      root.dataset.wrbState = result.counts.exported ? (result.counts.attention ? 'attention' : 'ready') : 'empty';
      updateCounts();
      if (announce && announcer) {
        clearTimeout(announceTimer);
        announceTimer = setTimeout(() => { announcer.textContent = announcement(result); }, 900);
      }
      if (announce && !reportedBuilt && result.counts.exported && !isSample(state)) {
        reportedBuilt = true;
        toolEvent('wix_redirects_built');
      }
    }

    // Typing waits for a pause; a long list waits a little longer.
    function schedule() {
      clearTimeout(computeTimer);
      const size = Object.values(fields).reduce((sum, input) => sum + (input ? input.value.length : 0), 0);
      computeTimer = setTimeout(() => changed(), size > 60000 ? 450 : 220);
    }

    function changed(focusTarget) {
      clearTimeout(computeTimer);
      hideFallback();
      setStatus('');
      render(true);
      if (focusTarget) restoreFocus(focusTarget);
    }

    function resetMode(mode) {
      memory[mode] = perMode();
      view.filter = 'all';
      view.limit = PAGE_SIZE;
      view.file = 0;
      view.waitingLimit = 50;
    }

    const rowByKey = key => result && (result.all.find(row => row.key === key) || result.unmatched.find(row => row.key === key));
    function patch(key, change) {
      const store = memory[modeValue()];
      const added = store.added.find(row => row.key === key);
      if (added) {
        if (change.removed) store.added = store.added.filter(row => row !== added);
        else {
          if (change.from != null) added.from = change.from;
          if (change.to != null) added.to = change.to;
          added.fixed = change.fixed || null;
        }
        return;
      }
      const next = { ...(store.overrides[key] || {}), ...change };
      if (!change.fixed && (change.from != null || change.to != null)) delete next.fixed;
      store.overrides[key] = next;
    }

    // Where focus is going, described so the same control can be found again
    // after the table is redrawn.
    function focusTarget(element) {
      if (!element || !root.contains(element) || !element.dataset) return null;
      const { key, wrbEdit, wrbAction, wrbFilter } = element.dataset;
      if (key && wrbEdit) return { key, side: wrbEdit };
      if (key && wrbAction) return { key, action: wrbAction };
      if (wrbFilter) return { selector: `[data-wrb-filter="${wrbFilter}"]` };
      if (wrbAction) return { selector: `[data-wrb-action="${wrbAction}"]` };
      if (element.id) return { selector: `#${element.id}` };
      return null;
    }

    // After a re-render the pressed button is gone: put focus back near it.
    function restoreFocus(target, fallback = true) {
      let element = null;
      if (target.key && target.side) element = findAll('[data-wrb-edit]').find(item => item.dataset.key === target.key && item.dataset.wrbEdit === target.side);
      if (!element && target.key && target.action) element = findAll(`[data-wrb-action="${target.action}"]`).find(item => item.dataset.key === target.key);
      if (!element && target.key) {
        const row = findAll('.wrb-row').find(item => item.dataset.key === target.key);
        element = row && row.querySelector('button');
      }
      if (!element && target.selector) element = find(target.selector);
      if (!element && fallback) element = find('[data-wrb-result-title]');
      if (element && typeof element.focus === 'function') element.focus({ preventScroll: false });
    }

    function startEdit(button) {
      const key = button.dataset.key;
      const side = button.dataset.wrbEdit;
      // Saving an edit in progress redraws the table, so find the cell again.
      if (editing) commitEdit(true);
      const cell = findAll('[data-wrb-edit]').find(item => item.dataset.key === key && item.dataset.wrbEdit === side);
      const row = rowByKey(key);
      if (!cell || !row) return;
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'wrb-cell-input';
      input.value = side === 'from' ? row.from : row.to;
      input.setAttribute('inputmode', 'url');
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('autocapitalize', 'off');
      input.setAttribute('spellcheck', 'false');
      input.setAttribute('dir', 'ltr');
      input.setAttribute('aria-label', `${side === 'from' ? 'Old' : 'New'} address on line ${row.line}. Press Enter to save, Escape to cancel.`);
      if (side === 'to' && result.mode === 'match') input.setAttribute('list', 'wrb-new-options');
      editing = { key, side, input, original: input.value };
      cell.replaceWith(input);
      input.focus();
      try { input.setSelectionRange(0, input.value.length); } catch (_) { /* not selectable */ }
    }

    function commitEdit(keep) {
      if (!editing) return;
      const { key, side, input, original } = editing;
      editing = null;
      if (input.value !== original) {
        patch(key, side === 'from' ? { from: input.value } : { to: input.value, accepted: true });
        changed(keep ? null : { key, side });
        // A new row's old address is in: go straight on to its new address.
        const row = !keep && side === 'from' && rowByKey(key);
        if (row && !text(row.to).trim()) {
          const next = findAll('[data-wrb-edit="to"]').find(item => item.dataset.key === key);
          if (next) startEdit(next);
        }
      } else {
        shown.clear();
        render(false);
        if (!keep) restoreFocus({ key, side });
      }
    }

    function cancelEdit() {
      if (!editing) return;
      const { key, side } = editing;
      editing = null;
      shown.clear();
      render(false);
      restoreFocus({ key, side });
    }

    const issueRows = id => result.rows.filter(row => row.issues.some(issue => issue.id === id));
    function flatten(row) {
      const chain = row.issues.find(issue => issue.id === 'chain');
      if (chain) patch(row.key, { to: chain.final, fixed: { kind: 'chain', before: row.to } });
    }
    function dropFragment(row, side) {
      const address = side === 'from' ? row.a : row.b;
      const raw = side === 'from' ? row.from : row.to;
      const at = raw.indexOf('#');
      if (at === -1) return;
      if (side === 'from') patch(row.key, { from: raw.slice(0, at), fixed: { kind: 'fragment', before: raw, side, removed: `#${address.fragment}` } });
      else patch(row.key, { to: raw.slice(0, at), fixed: { kind: 'fragment', before: raw, side, removed: `#${address.fragment}` } });
    }

    function act(button) {
      const action = button.dataset.wrbAction;
      const key = button.dataset.key;
      const value = button.dataset.wrbValue;
      const row = key ? rowByKey(key) : null;
      const store = memory[modeValue()];
      const nextRowKey = () => {
        const index = result.rows.findIndex(item => item.key === key);
        const after = result.rows[index + 1] || result.rows[index - 1];
        return after ? { key: after.key } : { selector: '[data-wrb-table-title]' };
      };
      switch (action) {
        case 'remove': {
          const target = nextRowKey();
          patch(key, { removed: true });
          return changed(target);
        }
        case 'keep': {
          result.rows.filter(item => item.key !== key && item.a.value === row.a.value).forEach(item => patch(item.key, { removed: true }));
          return changed({ key });
        }
        case 'flatten':
          if (row) flatten(row);
          return changed({ key });
        case 'flatten-all':
          issueRows('chain').forEach(flatten);
          return changed({ selector: '[data-wrb-result-title]' });
        case 'merge-all':
          issueRows('duplicate').forEach(item => patch(item.key, { removed: true }));
          return changed({ selector: '[data-wrb-result-title]' });
        case 'self-all':
          issueRows('self').forEach(item => patch(item.key, { removed: true }));
          return changed({ selector: '[data-wrb-result-title]' });
        case 'fragment':
          if (row) dropFragment(row, 'from');
          return changed({ key });
        case 'fragment-all':
          issueRows('fragment').forEach(item => dropFragment(item, 'from'));
          return changed({ selector: '[data-wrb-result-title]' });
        case 'fragment-new':
          if (row) dropFragment(row, 'to');
          return changed({ key });
        case 'fragment-new-all':
          issueRows('fragment-new').forEach(item => dropFragment(item, 'to'));
          return changed({ selector: '[data-wrb-result-title]' });
        case 'undo': {
          if (!row || !row.fixed) return undefined;
          const side = row.fixed.side || 'to';
          const store2 = store.overrides[key];
          if (store2) {
            delete store2.fixed;
            if (side === 'from') store2.from = row.fixed.before;
            else store2.to = row.fixed.before;
          } else patch(key, side === 'from' ? { from: row.fixed.before } : { to: row.fixed.before });
          return changed({ key });
        }
        case 'accept':
          patch(key, { accepted: true });
          return changed({ key });
        case 'pick': {
          const cell = findAll('[data-wrb-edit="to"]').find(item => item.dataset.key === key);
          if (cell) startEdit(cell);
          return undefined;
        }
        case 'dest': {
          const input = findAll('[data-wrb-dest]').find(item => item.dataset.key === key);
          if (!input || !input.value.trim()) {
            if (input) input.focus();
            return undefined;
          }
          patch(key, { to: input.value.trim(), accepted: true });
          return changed({ key, side: 'to' });
        }
        case 'group-use':
          if (!store.groups.includes(value)) store.groups.push(value);
          return changed({ selector: '[data-wrb-result-title]' });
        case 'group-undo':
          store.groups = store.groups.filter(item => item !== value);
          return changed({ selector: '[data-wrb-result-title]' });
        case 'use-site':
          if (site) site.value = value;
          return changed({ selector: '[data-wrb-site]' });
        case 'keep-header':
          store.keepHeader = true;
          return changed({ selector: '[data-wrb-result-title]' });
        case 'more':
          view.limit += PAGE_SIZE;
          shown.clear();
          return changed();
        case 'more-waiting':
          view.waitingLimit += 50;
          return changed();
        case 'show':
          view.filter = 'issue';
          view.issue = button.dataset.wrbIssue;
          view.limit = PAGE_SIZE;
          changed();
          scrollToTable();
          return undefined;
        case 'file':
          view.file = Number(value) || 0;
          return changed({ selector: `[data-wrb-action="file"][data-wrb-value="${view.file}"]` });
        case 'add-row': {
          addedCount += 1;
          const added = { key: `add-${addedCount}`, line: 'new', from: '', to: '' };
          store.added.push(added);
          view.filter = 'all';
          changed();
          const cell = findAll('[data-wrb-edit="from"]').find(item => item.dataset.key === added.key);
          if (cell) startEdit(cell);
          return undefined;
        }
        default:
          return undefined;
      }
    }

    function scrollToTable() {
      const table = find('[data-wrb-table-title]');
      if (!table) return;
      if (typeof table.scrollIntoView === 'function') table.scrollIntoView({ block: 'start' });
      table.focus({ preventScroll: true });
    }

    // ---- Copy and download ----
    const status = find('[data-wrb-status]');
    const setStatus = message => { if (status) status.textContent = message; };
    function hideFallback() {
      if (fallback) fallback.hidden = true;
    }
    async function copy() {
      if (!result || !result.counts.exported) return;
      const value = csvText(result, view.file);
      hideFallback();
      const message = result.files.length > 1 ? `File ${view.file + 1} copied.` : 'CSV copied.';
      try {
        if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(value);
        setStatus(message);
        toolEvent('wix_redirects_copied');
      } catch (_) {
        // Some browsers block the Clipboard API. Select the text in a box and
        // try the older copy command before asking for ⌘C / Ctrl+C.
        if (!fallback || !fallbackText) {
          setStatus('Copy the file’s text from the box below.');
          return;
        }
        fallbackText.value = value;
        fallback.hidden = false;
        fallbackText.focus();
        fallbackText.select();
        let copied = false;
        try { copied = typeof document.execCommand === 'function' && document.execCommand('copy'); } catch (__) { copied = false; }
        if (copied) {
          fallback.hidden = true;
          find('[data-wrb-copy]').focus();
          toolEvent('wix_redirects_copied');
        }
        setStatus(copied ? message : 'Couldn’t copy automatically. The text is selected in the box below, so copy it from there.');
      }
    }
    function revoke(url) {
      clearTimeout(urls.get(url));
      try { window.URL.revokeObjectURL(url); } catch (_) { /* already gone */ }
      urls.delete(url);
    }
    function download() {
      const file = result && currentFile(result, view.file);
      if (!file) return;
      if (!window.URL || typeof window.URL.createObjectURL !== 'function') {
        setStatus('Download isn’t available here. Copy the text instead and save it as a .csv file.');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([file.csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.hidden = true;
      root.append(link);
      link.click();
      link.remove();
      urls.set(url, setTimeout(() => revoke(url), 1000));
      setStatus(`Downloading ${file.name}.`);
      toolEvent('wix_redirects_downloaded');
    }

    // ---- Events ----
    root.addEventListener('input', event => {
      const target = event.target;
      if (target.matches('[data-wrb-input]')) {
        updateCounts();
        schedule();
      } else if (target === site || target.matches('[data-wrb-setting], [data-wrb-prefix]')) schedule();
    });
    root.addEventListener('change', event => {
      const target = event.target;
      if (target.name === 'wrb-mode') {
        commitEdit(true);
        view.filter = 'all';
        view.limit = PAGE_SIZE;
        view.file = 0;
        changed();
      } else if (target === platformSelect || target.matches('[data-wrb-setting]')) changed();
      else if (target.matches('[data-wrb-file]')) readFile(target);
    });
    root.addEventListener('click', event => {
      const edit = event.target.closest('[data-wrb-edit]');
      if (edit) {
        startEdit(edit);
        return;
      }
      const filter = event.target.closest('[data-wrb-filter]');
      if (filter) {
        if (filter.dataset.wrbFilter === 'issue') return;
        view.filter = filter.dataset.wrbFilter;
        view.issue = '';
        view.limit = PAGE_SIZE;
        changed({ selector: `[data-wrb-filter="${view.filter}"]` });
        return;
      }
      const sample = event.target.closest('[data-wrb-sample]');
      if (sample) {
        const mode = sample.dataset.wrbSample;
        resetMode(mode);
        if (mode === 'pairs') fields.pairs.value = SAMPLE;
        if (mode === 'match') {
          fields.old.value = SAMPLE_OLD;
          fields.new.value = SAMPLE_NEW;
        }
        if (mode === 'pattern') {
          fields.pattern.value = SAMPLE_PATTERN;
          if (platformSelect) platformSelect.value = 'shopify';
        }
        changed({ selector: '[data-wrb-result-title]' });
        return;
      }
      const clear = event.target.closest('[data-wrb-clear]');
      if (clear) {
        const mode = clear.dataset.wrbClear;
        resetMode(mode);
        if (mode === 'pairs') fields.pairs.value = '';
        if (mode === 'match') {
          fields.old.value = '';
          fields.new.value = '';
        }
        if (mode === 'pattern') fields.pattern.value = '';
        changed();
        const first = mode === 'match' ? fields.old : fields[mode];
        if (first) first.focus();
        return;
      }
      if (event.target.closest('[data-wrb-download]')) {
        download();
        return;
      }
      if (event.target.closest('[data-wrb-copy]')) {
        copy();
        return;
      }
      const action = event.target.closest('[data-wrb-action]');
      if (action) act(action);
    });
    root.addEventListener('keydown', event => {
      if (editing && event.target === editing.input) {
        if (event.key === 'Enter') {
          event.preventDefault();
          commitEdit(false);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          cancelEdit();
        }
        return;
      }
      if (event.key === 'Enter' && event.target.matches('[data-wrb-dest]')) {
        event.preventDefault();
        const button = event.target.parentElement.querySelector('[data-wrb-action="dest"]');
        if (button) act(button);
      }
    });
    // Leaving the box saves it, unless that edit already ended (Enter,
    // Escape) or another one started in the meantime.
    root.addEventListener('focusout', event => {
      if (!editing || event.target !== editing.input) return;
      const session = editing;
      const next = focusTarget(event.relatedTarget);
      setTimeout(() => {
        if (editing !== session) return;
        commitEdit(true);
        // Tab moved on to a control the redraw replaced: find it again.
        const now = document.activeElement;
        if (next && (!now || now === document.body || !root.contains(now))) restoreFocus(next, false);
      }, 0);
    });

    // The example in the box is selected on its first focus, so a paste
    // replaces it.
    let exampleArmed = true;
    fields.pairs.addEventListener('focus', () => {
      if (!exampleArmed) return;
      exampleArmed = false;
      if (fields.pairs.value === SAMPLE) {
        fields.pairs.select();
        try { fields.pairs.setSelectionRange(0, fields.pairs.value.length); } catch (_) { /* not selectable */ }
      }
    });

    // A file is read here, in the browser, and never uploaded.
    function readFile(input) {
      const file = input.files && input.files[0];
      if (!file) return;
      const note = find('[data-wrb-file-status]');
      if (file.size > 5 * 1024 * 1024) {
        if (note) note.textContent = 'That file is over 5 MB. Split it, or paste the rows instead.';
        input.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const content = text(reader.result);
        input.value = '';
        // A file saved in an older Excel format (Windows-1252) reads as
        // UTF-8 with � in place of letters like é: those addresses would be
        // wrong, so the file isn't used.
        if (content.includes('�')) {
          if (note) note.textContent = 'Some letters in this file didn’t read correctly (�). Save it as “CSV UTF-8” and choose it again.';
          return;
        }
        resetMode('pairs');
        fields.pairs.value = content;
        if (note) note.textContent = `Read ${file.name}.`;
        changed({ selector: '[data-wrb-result-title]' });
      };
      reader.onerror = () => {
        if (note) note.textContent = 'Couldn’t read that file. Paste the rows instead.';
      };
      reader.readAsText(file);
    }

    window.addEventListener('pagehide', () => {
      clearTimeout(computeTimer);
      clearTimeout(announceTimer);
      [...urls.keys()].forEach(revoke);
    });
    render(false);
  }

  return Object.freeze({
    META,
    SAMPLE,
    SAMPLES,
    PLATFORMS,
    DEFAULT_PREFIXES,
    PER_FILE,
    PER_SITE,
    CSV_HEADER,
    STATUS_WORDS,
    ISSUES,
    parseInput,
    readAddress,
    analyze,
    flattenChains,
    findGroups,
    buildCsv,
    splitFiles,
    extractAddresses,
    matchLists,
    suggestFromPattern,
    rulesMarkup,
    compute,
    describe,
    directoryPreview,
    summaryText,
    nextText,
    inputNotes,
    views,
    rowNotes,
    announcement,
    hintText,
    esc,
    init
  });
});
